<!-- 
  TopNav + Sidebar + Router View layout component.
-->
<template>
  <div>
    <nav class="topnav" ref="formContainer">
      <div class="topnav-inner">

        <!-- Logo -->
        <router-link class="topnav-logo" :to="logoDestination">
          <img
            :src="business.logo ? url.hostName + business.logo : require('./private-assets/images/logo.png')"
            alt="Logo"
            class="logo-img"
          />
        </router-link>

        <!-- Current page title -->
        <div class="topnav-pagetitle">
          {{ $router.currentRoute.value.name }}
        </div>

        <!-- Right side -->
        <div class="topnav-right">

          <!-- Subscribe badge -->
          <router-link v-if="freemium && user && !essActive" :to="$url('/subscribe')" class="btn-subscribe">
            <font-awesome-icon icon="star" />
            Subscribe
          </router-link>

          <!-- Clock In / Out -->
          <div v-if="user" class="clockin-wrap">
            <!-- Elapsed timer — only shown when clocked in -->
            <div v-if="clockedIn" class="clockin-timer">
              <span class="clockin-elapsed">{{ elapsedTime }}</span>
            </div>
            <!-- Button -->
            <button
              class="btn-clockin"
              :class="clockedIn ? 'btn-clockin--out' : 'btn-clockin--in'"
              :disabled="clockLoading || (!clockedIn && !clockWindowOpen)"
              :title="!clockedIn && !clockWindowOpen ? 'Clock-in opens at ' + clockWindowOpensAt : ''"
              @click="openClockModal"
            >
              <span v-if="clockLoading" class="clockin-spinner"></span>
              <span v-else-if="!clockedIn && !clockWindowOpen">Opens {{ clockWindowOpensAt }}</span>
              <span v-else>{{ clockedIn ? 'Clock Out' : 'Clock In' }}</span>
            </button>
          </div>

          <!-- Chat Bell -->
          <chat-bell v-if="user" />

          <!-- Notification Bell -->
          <notification-bell v-if="user" />

          <!-- User dropdown -->
          <div class="user-dropdown" :class="{ open: dropdownOpen }" v-click-outside="closeDropdown">
            <button class="user-trigger" @click="dropdownOpen = !dropdownOpen">
              <div class="user-avatar">{{ userInitials }}</div>
              <div class="user-info">
                <span class="user-name">
                  {{ user && user.first_name ? `${user.first_name} ${user.last_name}` : 'Loading...' }}
                </span>
                <span class="user-role" v-if="user && user.roles && user.roles.length">
                  {{ user.roles[0] && user.roles[0].name }}
                </span>
              </div>
              <font-awesome-icon icon="chevron-down" class="dropdown-chevron" :class="{ rotated: dropdownOpen }" />
            </button>

            <div class="user-menu" v-show="dropdownOpen">
              <div class="user-menu-profile">
                <div class="user-menu-avatar">{{ userInitials }}</div>
                <div class="user-menu-details">
                  <div class="user-menu-name">
                    {{ user && user.first_name ? `${user.first_name} ${user.last_name}` : 'Loading...' }}
                  </div>
                  <div class="user-menu-role" v-if="user && user.roles && user.roles.length">
                    {{ user.roles[0] && user.roles[0].name }}
                  </div>
                </div>
              </div>
              <div class="user-menu-divider"></div>
              <button
                class="user-menu-item user-menu-item--danger"
                data-bs-toggle="modal"
                data-bs-target="#logoutmodal"
                @click="closeDropdown"
              >
                <font-awesome-icon icon="sign-out-alt" class="user-menu-icon" />
                Sign Out
              </button>
            </div>
          </div>

          <!-- Mobile menu toggle -->
          <button class="mobile-toggle" @click="toggleSidebar" aria-label="Toggle menu">
            <font-awesome-icon :icon="sidebarOpen ? 'times' : 'bars'" />
          </button>

        </div>
      </div>
    </nav>

    <!-- Overlay (mobile) -->
    <div class="sidebar-overlay" :class="{ 'overlay-open': sidebarOpen }" @click="closeSidebar"></div>

    <div class="container-fluid" style="margin-top: 72px;">
      <div class="row flex-nowrap">

        <!-- SIDEBAR -->
        <div class="col-auto px-0" style="width:240px; min-width:240px; background:#051B35;">
          <div class="sidebar-fixed" :class="{ 'sidebar-open': sidebarOpen }">

            <!-- Company Switcher -->
            <div v-if="companies.length" class="px-3 pt-3 pb-2">
              <div class="dropdown w-100">
                <button class="company-switcher-btn dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <span class="company-green-dot"></span>
                  <span class="company-name-text">{{ $truncate(active_company.name, 20) }}</span>
                  <span class="active-pill">active</span>
                </button>
                <ul class="dropdown-menu company-dropdown-menu">
                  <li v-for="company in companies" :key="company.id">
                    <a class="dropdown-item company-dropdown-item"
                      @click="active_company.id != company.id ? changeCompany(company.id) : null"
                      style="cursor:pointer;">
                      <span class="company-dot-sm" :class="company.id == active_company.id ? 'dot-active' : ''"></span>
                      {{ company.name }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="sidebar-hr"></div>

            <!-- Nav Links -->
            <ul v-if="permission" class="nav flex-column px-2 sidebar-nav-list" id="menu">

              <li class="nav-item">
                <router-link :to="logoDestination" class="sidebar-link" :class="$route.name == 'Dashboard' ? 'sidebar-link--active' : ''" @click="closeSidebar">
                  <font-awesome-icon icon="home" class="sidebar-link-icon" />
                  <span>Dashboard</span>
                </router-link>
              </li>

              <li class="nav-item" v-if="(permission?.administration_module || permission?.view_store_management) && (!planCan || planCan.administrator_access)">
                <router-link :to="$url('/company-info')" class="sidebar-link" @click="closeSidebar"
                  :class="['Manage User','Create User','Edit User','All Business','Create Business','Edit Business',
                  'Edit Company Info','Company Info','Manage Workflow Setup','Manage Business Bank Accounts',
                  'Manage All Banks','Manage Pension Providers'].includes($router.currentRoute.value.name) ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="user" class="sidebar-link-icon" />
                  <span>Administration</span>
                </router-link>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.inventory_access) && (
                permission?.inventory_mgt_module ||
                permission?.view_product ||
                permission?.view_purchase ||
                permission?.view_sales ||
                permission?.view_debtors ||
                permission?.view_creditors ||
                permission?.view_reports ||
                permission?.view_settings ||
                permission?.sales_module ||
                permission?.purchase_module ||
                permission?.debtors_module ||
                permission?.creditors_module ||
                permission?.report_module ||
                permission?.settings_module ||
                permission?.view_internal_issue
              )">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[inventoryOpen ? 'sidebar-link--open' : '', inventoryActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('inventoryOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="boxes" class="sidebar-link-icon" />
                  <span>Inventory Module</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': inventoryOpen }" />
                </button>
                <div class="sidebar-children" v-show="inventoryOpen">
                  <router-link :to="$url('/inventory/dashboard')" class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link v-if="permission?.view_product" :to="$url('/all-products')"  class="sidebar-child" @click="closeSidebar">Products</router-link>
                  <router-link v-if="(permission?.sales_module || permission?.view_sales) && (!planCan || planCan.sales_access)"         :to="$url('/all-sales')"     class="sidebar-child" @click="closeSidebar">Sales</router-link>
                  <router-link v-if="(permission?.purchase_module || view_purchase) && (!planCan || planCan.purchase_access)"                                    :to="$url('/all-purchases')" class="sidebar-child" @click="closeSidebar">Purchase</router-link>
                  <router-link v-if="(permission?.debtors_module || permission?.view_debtors == 1) && (!planCan || planCan.debitor_access)"    :to="$url('/debtor')"        class="sidebar-child" @click="closeSidebar">Debtors</router-link>
                  <router-link v-if="(permission?.creditors_module || permission?.view_creditors == 1) && (!planCan || planCan.creditor_access)" :to="$url('/creditor')"      class="sidebar-child" @click="closeSidebar">Creditors</router-link>
                  <router-link v-if="permission?.view_internal_issue == 1" :to="$url('/all-internal-issues')" class="sidebar-child" @click="closeSidebar">Internal Requisitions</router-link>
                  <router-link v-if="permission?.report_module || permission?.view_reports" :to="$url('/all-reports')"      class="sidebar-child" @click="closeSidebar">Reports</router-link>
                  <router-link v-if="permission?.settings_module || permission?.view_settings" :to="$url('/manage-store')"   class="sidebar-child" @click="closeSidebar">Settings</router-link>
                </div>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.accounting_access) && (permission?.accounting_module || permission?.view_expense || permission?.view_journal || permission?.view_ledger || permission?.view_transaction || permission?.wallet_module || permission?.view_wallet)">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[accountingOpen ? 'sidebar-link--open' : '', accountingActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('accountingOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="calculator" class="sidebar-link-icon" />
                  <span>Accounting Module</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': accountingOpen }" />
                </button>
                <div class="sidebar-children" v-show="accountingOpen">
                  <router-link v-if="permission?.accounting_module"                                     :to="$url('/accounting/dashboard')" class="sidebar-child" @click="closeSidebar">Accounting Dashboard</router-link>
                  <router-link v-if="permission?.accounting_module || permission?.view_journal"           :to="$url('/manage-journals')"      class="sidebar-child" @click="closeSidebar">Manage Journals</router-link>
                  <router-link v-if="permission?.accounting_module || permission?.view_ledger"            :to="$url('/manage-ledgers')"       class="sidebar-child" @click="closeSidebar">Manage Ledgers</router-link>
                  <router-link v-if="permission?.accounting_module || permission?.view_transaction"  :to="$url('/transactions')"         class="sidebar-child" @click="closeSidebar">Transactions</router-link>
                  <router-link v-if="permission?.accounting_module || permission?.view_po_payments"     :to="$url('/accounting/vendor-payments')" class="sidebar-child" @click="closeSidebar">Vendor Payments</router-link>
                  <!-- <router-link v-if="permission?.accounting_module"                                     :to="$url('/accounting/payment-vouchers')" class="sidebar-child" @click="closeSidebar">Payment Vouchers</router-link>
                  <router-link v-if="permission?.accounting_module"                                     :to="$url('/accounting/bills')" class="sidebar-child" @click="closeSidebar">Bills</router-link>
                  <router-link v-if="permission?.accounting_module"                                     :to="$url('/accounting/petty-cash')" class="sidebar-child" @click="closeSidebar">Petty Cash</router-link> -->
                  <router-link v-if="permission?.view_bank == 1"                                     :to="$url('/accounting/bank-accounts-setup')" class="sidebar-child" @click="closeSidebar">Bank Accounts</router-link>
                  <router-link v-if="permission?.accounting_module"                                     :to="$url('/accounting/fiscal-years')" class="sidebar-child" @click="closeSidebar">Fiscal Years</router-link>
                  <router-link v-if="permission?.accounting_module"                                     :to="$url('/balance-sheet-report')" class="sidebar-child" @click="closeSidebar">Accounting Reports</router-link>
                  <router-link v-if="permission?.wallet_module || permission?.view_wallet"             :to="$url('/wallet')"               class="sidebar-child" @click="closeSidebar">Business Wallet</router-link>
                  <router-link v-if="permission?.view_expense == 1"                                     :to="$url('/manage-expenses')"      class="sidebar-child" @click="closeSidebar">Manage Expenses</router-link>
                </div>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.hr_access) && (permission?.hr_module || permission?.payroll_module || permission?.view_employee || permission?.view_recruitment || permission?.manage_announcements)">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[hrOpen ? 'sidebar-link--open' : '', hrActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('hrOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="users" class="sidebar-link-icon" />
                  <span>HR &amp; Payroll Module</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': hrOpen }" />
                </button>
                <div class="sidebar-children" v-show="hrOpen">

                  <!-- ── Employees ── -->
                  <button v-if="permission?.view_employee"
                    class="sidebar-sub-toggle"
                    :class="hrEmployeesOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrEmployeesOpen')"
                    type="button"
                  >
                    <!-- <font-awesome-icon icon="id-badge" class="sub-toggle-icon" /> -->
                    <span>Employees</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrEmployeesOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrEmployeesOpen">
                    <router-link v-if="permission?.view_employee" :to="$url('/employees-dashboard')"    class="sidebar-grandchild" @click="closeSidebar">Dashboard</router-link>
                    <router-link v-if="permission?.view_employee" :to="$url('/manage-employees')"       class="sidebar-grandchild" @click="closeSidebar">All Employees</router-link>
                    <router-link v-if="permission?.view_employee" :to="$url('/manage-employees-invites')" class="sidebar-grandchild" @click="closeSidebar">Invites</router-link>
                  </div>

                  <!-- ── Payroll ── -->
                  <button
                    v-if="(permission?.payroll_module || permission?.view_payroll == 1) && (!planCan || planCan.payroll_access)"
                    class="sidebar-sub-toggle"
                    :class="hrPayrollOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrPayrollOpen')"
                    type="button"
                  >
                    <!-- <font-awesome-icon icon="money-check-alt" class="sub-toggle-icon" /> -->
                    <span>Payroll</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrPayrollOpen }" />
                  </button>
                  <div v-if="(permission?.payroll_module || permission?.view_payroll == 1) && (!planCan || planCan.payroll_access)" class="sidebar-grandchildren" v-show="hrPayrollOpen">
                    <router-link v-if="permission?.view_payroll" :to="$url('/payroll/dashboard')" class="sidebar-grandchild" @click="closeSidebar">Dashboard</router-link>
                    <router-link v-if="permission?.view_payroll" :to="$url('/payroll-periods')"   class="sidebar-grandchild" @click="closeSidebar">Run History</router-link>
                    <router-link v-if="permission?.view_payroll" :to="$url('/payroll-history')"   class="sidebar-grandchild" @click="closeSidebar">Payslip History</router-link>
                    <router-link v-if="permission?.create_payroll" :to="$url('/create-payroll')"    class="sidebar-grandchild" @click="closeSidebar">Run Payroll</router-link>
                    <router-link v-if="permission?.view_payroll" :to="$url('/payroll-additional-earnings')"   class="sidebar-grandchild" @click="closeSidebar">Earnings</router-link>
                    <router-link v-if="permission?.view_payroll" :to="$url('/payroll-additional-deductions')" class="sidebar-grandchild" @click="closeSidebar">Deductions</router-link>
                  </div>

                  <!-- ── Attendance ── -->
                  <button v-if="permission?.view_attendance || permission?.view_shifts == 1 || permission?.view_rota == 1"
                    class="sidebar-sub-toggle"
                    :class="hrAttendanceOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrAttendanceOpen')"
                    type="button"
                  >
                    <!-- <font-awesome-icon icon="clock" class="sub-toggle-icon" /> -->
                    <span>Attendance</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrAttendanceOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrAttendanceOpen">
                    <router-link v-if="permission?.view_attendance" :to="$url('/hr/attendance')" class="sidebar-grandchild" @click="closeSidebar">Attendance Log</router-link>
                    <router-link v-if="permission?.view_shifts" :to="$url('/hr/shifts')"     class="sidebar-grandchild" @click="closeSidebar">Shifts</router-link>
                    <router-link v-if="permission?.view_rota" :to="$url('/hr/rota')"       class="sidebar-grandchild" @click="closeSidebar">Rotas</router-link>
                  </div>

                  <!-- ── Leave ── -->
                  <button  v-if="permission?.view_leave || permission?.manage_leave"
                    class="sidebar-sub-toggle"
                    :class="hrLeaveOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrLeaveOpen')"
                    type="button"
                  >
                    <!-- <font-awesome-icon icon="umbrella-beach" class="sub-toggle-icon" /> -->
                    <span>Leave</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrLeaveOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrLeaveOpen">
                    <router-link v-if="permission?.view_leave" :to="$url('/hr/leave')"          class="sidebar-grandchild" @click="closeSidebar">Leave Requests</router-link>
                    <router-link v-if="permission?.manage_leave" :to="$url('/hr/leave-balances')" class="sidebar-grandchild" @click="closeSidebar">Leave Balances</router-link>
                  </div>

                  <!-- ── Loans ── -->
                  <router-link v-if="permission?.view_loans" :to="$url('/hr/loans')" class="sidebar-sub-toggle" @click="closeSidebar">
                    <span>Loans</span>
                  </router-link>

                  <!-- ── Recruitment ── -->
                  <button v-if="permission?.view_recruitment"
                    class="sidebar-sub-toggle"
                    :class="hrRecruitmentOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrRecruitmentOpen')"
                    type="button"
                  >
                    <span>Recruitment</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrRecruitmentOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrRecruitmentOpen">
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'plans' } }"        class="sidebar-grandchild" @click="closeSidebar">Workforce Plans</router-link>
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'requisitions' } }" class="sidebar-grandchild" @click="closeSidebar">Job Requisitions</router-link>
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'postings' } }"     class="sidebar-grandchild" @click="closeSidebar">Job Postings</router-link>
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'applications' } }" class="sidebar-grandchild" @click="closeSidebar">Applications</router-link>
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'interviews' } }"   class="sidebar-grandchild" @click="closeSidebar">Interviews</router-link>
                    <router-link :to="{ name: 'HR Recruitment', params: { companyCode: $route.params.companyCode }, query: { tab: 'offers' } }"       class="sidebar-grandchild" @click="closeSidebar">Offer Letters</router-link>
                  </div>

                  <!-- ── Announcements ── -->
                  <router-link v-if="permission?.manage_announcements" :to="$url('/hr/announcements')" class="sidebar-sub-toggle" @click="closeSidebar">
                    <span>Announcements</span>
                  </router-link>

                  <!-- ── Documents ── -->
                  <router-link v-if="permission?.view_documents || permission?.upload_documents || permission?.hr_module" :to="$url('/hr/documents')" class="sidebar-sub-toggle" @click="closeSidebar">
                    <span>Documents</span>
                  </router-link>

                  <!-- ── Chat ── -->
                  <router-link :to="$url('/hr/chat')" class="sidebar-sub-toggle" @click="closeSidebar">
                    <span>Chat</span>
                  </router-link>

                  <!-- ── Performance Management (PMS) ── -->
                  <button v-if="permission?.view_pms_cycles || permission?.view_pms_reviews || permission?.manage_pms_bonus || permission?.view_pms_reports"
                    class="sidebar-sub-toggle"
                    :class="hrPmsOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrPmsOpen')"
                    type="button"
                  >
                    <span>Performance</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrPmsOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrPmsOpen">
                    <router-link v-if="permission?.view_pms_cycles"      :to="$url('/hr/performance/cycles')"       class="sidebar-grandchild" @click="closeSidebar">Appraisal Cycles</router-link>
                    <router-link v-if="permission?.view_pms_goals"       :to="$url('/hr/performance/goals')"        class="sidebar-grandchild" @click="closeSidebar">Goals</router-link>
                    <router-link v-if="permission?.manage_pms_kpis"      :to="$url('/hr/performance/kpi-library')"  class="sidebar-grandchild" @click="closeSidebar">KPI Library</router-link>
                    <router-link v-if="permission?.manage_pms_competencies" :to="$url('/hr/performance/competencies')" class="sidebar-grandchild" @click="closeSidebar">Competencies</router-link>
                    <router-link v-if="permission?.view_pms_reviews"     :to="$url('/hr/performance/reviews')"      class="sidebar-grandchild" @click="closeSidebar">Reviews</router-link>
                    <router-link v-if="permission?.manage_pms_bonus"     :to="$url('/hr/performance/bonus-engine')" class="sidebar-grandchild" @click="closeSidebar">Bonus Engine</router-link>
                    <router-link v-if="permission?.sync_pms_payroll"     :to="$url('/hr/performance/payroll-sync')" class="sidebar-grandchild" @click="closeSidebar">Payroll Sync</router-link>
                    <router-link v-if="permission?.view_pms_reports"     :to="$url('/hr/performance/reports')"      class="sidebar-grandchild" @click="closeSidebar">Reports</router-link>
                  </div>

                  <!-- ── Training ── -->
                  <button v-if="permission?.training_module || permission?.manage_training_courses || permission?.training_self_access"
                    class="sidebar-sub-toggle"
                    :class="hrTrainingOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrTrainingOpen')"
                    type="button"
                  >
                    <span>Training</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrTrainingOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrTrainingOpen">
                    <router-link :to="$url('/hr/training/dashboard')"    class="sidebar-grandchild" @click="closeSidebar">Dashboard</router-link>
                    <router-link v-if="permission?.manage_training_courses" :to="$url('/hr/training/courses')"  class="sidebar-grandchild" @click="closeSidebar">Courses</router-link>
                    <router-link v-if="permission?.manage_training_assignments" :to="$url('/hr/training/assignments')" class="sidebar-grandchild" @click="closeSidebar">Assignments</router-link>
                    <router-link v-if="permission?.manage_training_assignments" :to="$url('/hr/training/enrolments')"  class="sidebar-grandchild" @click="closeSidebar">Enrolments</router-link>
                  </div>

                  <!-- ── Exit Management ── -->
                  <button v-if="permission?.exits_module || permission?.manage_exits || permission?.view_exit_reports"
                    class="sidebar-sub-toggle"
                    :class="hrExitsOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrExitsOpen')"
                    type="button"
                  >
                    <span>Exit Management</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrExitsOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrExitsOpen">
                    <router-link v-if="permission?.manage_exits"                              :to="$url('/hr/exits/pipeline')" class="sidebar-grandchild" @click="closeSidebar">Pipeline</router-link>
                    <router-link v-if="permission?.manage_exits || permission?.view_exit_reports" :to="$url('/hr/exits')"     class="sidebar-grandchild" @click="closeSidebar">All Cases</router-link>
                    <router-link v-if="permission?.manage_exit_settings"                      :to="$url('/hr/exits/settings')" class="sidebar-grandchild" @click="closeSidebar">Settings</router-link>
                  </div>

                  <!-- ── Settings ── -->
                  <button v-if="permission?.view_department || permission?.view_position == 1 || permission?.view_paygrade == 1 || permission?.view_salary_component || permission?.view_public_holiday == 1 || permission?.view_client == 1"
                    class="sidebar-sub-toggle"
                    :class="hrSettingsOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click="toggleHrMenu('hrSettingsOpen')"
                    type="button"
                  >
                    <!-- <font-awesome-icon icon="cog" class="sub-toggle-icon" /> -->
                    <span>Settings</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': hrSettingsOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="hrSettingsOpen">
                    <router-link v-if="permission?.view_department" :to="$url('/manage-departments')" class="sidebar-grandchild" @click="closeSidebar">Departments</router-link>
                    <router-link v-if="permission?.view_department" :to="$url('/manage-units')"       class="sidebar-grandchild" @click="closeSidebar">Units</router-link>
                    <router-link v-if="permission?.view_position" :to="$url('/manage-positions')"   class="sidebar-grandchild" @click="closeSidebar">Positions</router-link>
                    <router-link v-if="permission?.view_paygrade" :to="$url('/manage-paygrade')"    class="sidebar-grandchild" @click="closeSidebar">Pay Grades</router-link>
                    <router-link v-if="permission?.view_salary_component" :to="$url('/salary-components')"  class="sidebar-grandchild" @click="closeSidebar">Salary Components</router-link>
                    <router-link v-if="permission?.view_public_holiday" :to="$url('/hr/public-holidays')" class="sidebar-grandchild" @click="closeSidebar">Public Holidays</router-link>
                    <router-link v-if="permission?.view_client" :to="$url('/hr/clients')"        class="sidebar-grandchild" @click="closeSidebar">Clients</router-link>
                  </div>

                </div>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.procurement_access) && (permission?.view_vendors || permission?.manage_vendors || permission?.view_purchase_requisitions || permission?.view_sourcing_events || permission?.view_procurement_categories || permission?.manage_procurement_categories || permission?.view_purchase_orders)">
                <div class="sidebar-link sidebar-link--split" :class="procurementActive ? 'sidebar-link--active' : ''">
                  <router-link :to="$url('/procurement/dashboard')" class="sidebar-link-main" @click="closeSidebar">
                    <font-awesome-icon icon="handshake" class="sidebar-link-icon" />
                    <span>Procurement</span>
                  </router-link>
                  <button class="sidebar-link-chevron" type="button" @click="toggleMenu('procurementOpen')" title="Expand">
                    <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': procurementOpen }" />
                  </button>
                </div>
                <div class="sidebar-children" v-show="procurementOpen">
                  <router-link :to="$url('/procurement/dashboard')" class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link v-if="permission?.view_vendors || permission?.manage_vendors" :to="$url('/procurement/vendors')" class="sidebar-child" @click="closeSidebar">Vendor Directory</router-link>
                  <router-link v-if="permission?.view_procurement_categories || permission?.manage_procurement_categories" :to="$url('/procurement/vendor-categories')" class="sidebar-child" @click="closeSidebar">Vendor Categories</router-link>

                  <router-link v-if="permission?.view_purchase_requisitions" :to="$url('/procurement/purchase-requisitions')" class="sidebar-child" @click="closeSidebar">Purchase Requisitions</router-link>
                  <router-link v-if="permission?.view_purchase_orders" :to="$url('/procurement/purchase-orders')" class="sidebar-child" @click="closeSidebar">Purchase Orders</router-link>
                  <router-link v-if="permission?.view_sourcing_events" :to="$url('/procurement/sourcing-events')" class="sidebar-child" @click="closeSidebar">Sourcing Events</router-link>
                </div>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.crm_access) && (permission?.crm_module || permission?.view_leads || permission?.view_contacts || permission?.view_opportunities || permission?.view_crm_activities || permission?.view_crm_accounts || permission?.view_segments || permission?.view_campaigns || permission?.view_journeys || permission?.view_cases || permission?.view_loyalty || permission?.view_surveys)">
                <div class="sidebar-link sidebar-link--split" :class="crmActive ? 'sidebar-link--active' : ''">
                  <router-link :to="$url('/crm/dashboard')" class="sidebar-link-main" @click="closeSidebar">
                    <font-awesome-icon icon="users" class="sidebar-link-icon" />
                    <span>CRM</span>
                  </router-link>
                  <button class="sidebar-link-chevron" type="button" @click="toggleMenu('crmOpen')" title="Expand">
                    <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': crmOpen }" />
                  </button>
                </div>
                <div class="sidebar-children" v-show="crmOpen">
                  <router-link :to="$url('/crm/dashboard')" class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link v-if="permission?.crm_module || permission?.view_leads" :to="$url('/crm/leads')" class="sidebar-child" @click="closeSidebar">Leads</router-link>
                  <router-link v-if="permission?.crm_module || permission?.view_contacts" :to="$url('/crm/contacts')" class="sidebar-child" @click="closeSidebar">Contacts</router-link>
                  <router-link v-if="permission?.crm_module || permission?.view_crm_accounts" :to="$url('/crm/accounts')" class="sidebar-child" @click="closeSidebar">Accounts</router-link>
                  <router-link v-if="permission?.crm_module || permission?.view_opportunities" :to="$url('/crm/opportunities')" class="sidebar-child" @click="closeSidebar">Opportunities</router-link>
                  <router-link v-if="permission?.crm_module || permission?.view_crm_activities" :to="$url('/crm/activities')" class="sidebar-child" @click="closeSidebar">Activities</router-link>
                  <router-link v-if="permission?.view_segments" :to="$url('/crm/segments')" class="sidebar-child" @click="closeSidebar">Segments</router-link>
                  <router-link v-if="permission?.view_campaigns" :to="$url('/crm/campaigns')" class="sidebar-child" @click="closeSidebar">Campaigns</router-link>
                  <router-link v-if="permission?.view_journeys" :to="$url('/crm/journeys')" class="sidebar-child" @click="closeSidebar">Journeys</router-link>
                  <router-link v-if="permission?.view_cases" :to="$url('/crm/cases')" class="sidebar-child" @click="closeSidebar">Cases</router-link>
                  <router-link v-if="permission?.view_loyalty" :to="$url('/crm/loyalty')" class="sidebar-child" @click="closeSidebar">Loyalty</router-link>
                  <router-link v-if="permission?.view_surveys" :to="$url('/crm/surveys')" class="sidebar-child" @click="closeSidebar">Surveys</router-link>
                </div>
              </li>

              <!-- Project Module -->
              <li class="nav-item" v-if="(!planCan || planCan.project_access) && (permission?.view_projects || permission?.manage_projects || permission?.view_timesheets || permission?.view_project_budgets || permission?.view_project_costs || permission?.manage_billing_proposals)">
                <div class="sidebar-link sidebar-link--split" :class="projectModuleActive ? 'sidebar-link--active' : ''">
                  <router-link :to="$url('/projects/dashboard')" class="sidebar-link-main" @click="closeSidebar">
                    <font-awesome-icon icon="diagram-project" class="sidebar-link-icon" />
                    <span>Project Module</span>
                  </router-link>
                  <button class="sidebar-link-chevron" type="button" @click="toggleMenu('projectModuleOpen')" title="Expand">
                    <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': projectModuleOpen }" />
                  </button>
                </div>
                <div class="sidebar-children" v-show="projectModuleOpen">
                  <router-link :to="$url('/projects/dashboard')" class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link :to="$url('/projects')" class="sidebar-child" @click="closeSidebar">Projects</router-link>
                  <router-link v-if="permission?.view_timesheets || permission?.manage_timesheets" :to="$url('/timesheets')" class="sidebar-child" @click="closeSidebar">Timesheets</router-link>
                </div>
              </li>

              <li class="nav-item" v-if="(!planCan || planCan.asset_access) && permission?.view_assets">
                <router-link :to="$url('/assets')" class="sidebar-link" @click="closeSidebar"
                  :class="assetModuleActive ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="boxes-stacked" class="sidebar-link-icon" />
                  <span>Asset Module</span>
                </router-link>
              </li>

              <!-- ── Visitors ── -->
              <li class="nav-item" v-if="(!planCan || planCan.visitors_access) && (permission?.visitors_module)">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[visitorsOpen ? 'sidebar-link--open' : '', visitorsActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('visitorsOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="id-badge" class="sidebar-link-icon" />
                  <span>Visitors Module</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': visitorsOpen }" />
                </button>
                <div class="sidebar-children" v-show="visitorsOpen">
                  <router-link v-if="permission?.manage_visitors || permission?.view_visitor_reports" :to="$url('/hr/visitors/dashboard')"  class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/active')"      class="sidebar-child" @click="closeSidebar">Active Visitors</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/roll-call')"   class="sidebar-child" @click="closeSidebar">Roll-Call</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/evacuation')"  class="sidebar-child" @click="closeSidebar">Evacuation Mode</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/check-in')"    class="sidebar-child" @click="closeSidebar">Check-In</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/pre-register')" class="sidebar-child" @click="closeSidebar">Pre-Register Visitor</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/group-visits')" class="sidebar-child" @click="closeSidebar">Group Visits</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/approvals')"   class="sidebar-child" @click="closeSidebar">Approvals</router-link>
                  <router-link v-if="permission?.view_visitor_reports"    :to="$url('/hr/visitors/history')"     class="sidebar-child" @click="closeSidebar">History</router-link>
                  <router-link v-if="permission?.view_visitor_reports"    :to="$url('/hr/visitors/reports')"     class="sidebar-child" @click="closeSidebar">Reports</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/directory')"   class="sidebar-child" @click="closeSidebar">Visitor Directory</router-link>
                  <router-link v-if="permission?.manage_visitors"         :to="$url('/hr/visitors/watchlist')"   class="sidebar-child" @click="closeSidebar">Watchlist</router-link>
                  <router-link v-if="permission?.view_visitor_reports"    :to="$url('/hr/visitors/audit')"       class="sidebar-child" @click="closeSidebar">Audit Trail</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/types')"       class="sidebar-child" @click="closeSidebar">Visitor Types</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/sites')"       class="sidebar-child" @click="closeSidebar">Sites &amp; Zones</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/lockers')"     class="sidebar-child" @click="closeSidebar">Lockers</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/documents')"   class="sidebar-child" @click="closeSidebar">Documents</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/dsr')"         class="sidebar-child" @click="closeSidebar">Data Subject Rights</router-link>
                  <router-link v-if="permission?.manage_visitor_settings" :to="$url('/hr/visitors/settings')"    class="sidebar-child" @click="closeSidebar">Settings</router-link>
                </div>
              </li>

              <li class="nav-item" v-if="permission?.invoice_module || permission?.view_invoice == 1">
                <router-link :to="$url('/invoice-history')" class="sidebar-link" @click="closeSidebar"
                  :class="['Invoices','Create Invoice','Invoice Detail','Record Invoice Payment','Invoice Payment History','Edit Invoice'].includes($router.currentRoute.value.name) ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="file-invoice" class="sidebar-link-icon" />
                  <span>Invoice Module</span>
                </router-link>
              </li>

              <li class="nav-item" v-if="permission?.billing_module || permission?.view_billing == 1">
                <router-link :to="$url('/manage-billing')" class="sidebar-link" @click="closeSidebar"
                  :class="['Manage Billing','Create Billing','Billing Detail','Billing Groups','Billing Customers','Manage Group Customers'].includes($router.currentRoute.value.name) ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="credit-card" class="sidebar-link-icon" />
                  <span>Billing Module</span>
                </router-link>
              </li>

              <!-- <li class="nav-item" v-if="permission?.report_module && (!planCan || planCan.hr_access)">
                <router-link :to="$url('/workflow-approvals')" class="sidebar-link" @click="closeSidebar"
                  :class="$route.name && $route.name.includes('Manage Workflow Approvals') ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="clipboard" class="sidebar-link-icon" />
                  <span>Approvals</span>
                </router-link>
              </li> -->

              <li v-if="permission?.manage_subscription" class="nav-item">
                <router-link :to="$url('/subscription-plan')" class="sidebar-link" @click="closeSidebar"
                  :class="['Subscribe','Subscription Plan'].includes($router.currentRoute.value.name) ? 'sidebar-link--active' : ''">
                  <font-awesome-icon icon="sync-alt" class="sidebar-link-icon" />
                  <span>Manage Subscription</span>
                </router-link>
              </li>

              <!-- ── Developer ────────────────────────────────────────────── -->
              <li class="nav-item" v-if="(!planCan || planCan.developer_access) && permission?.manage_api_keys">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[developerOpen ? 'sidebar-link--open' : '', developerActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('developerOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="code" class="sidebar-link-icon" />
                  <span>Developer</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': developerOpen }" />
                </button>
                <div class="sidebar-children" v-show="developerOpen">
                  <router-link :to="$url('/developer/api-keys')"  class="sidebar-child" @click="closeSidebar">API Keys</router-link>
                  <router-link :to="$url('/developer/webhooks')"  class="sidebar-child" @click="closeSidebar">Webhooks</router-link>
                  <router-link :to="$url('/developer/docs')"      class="sidebar-child" @click="closeSidebar">Integration Guide</router-link>
                </div>
              </li>

            </ul>

            <!-- ── Employee Self Service (always visible — personal portal, not a plan feature) ── -->
            <ul class="nav flex-column px-2 sidebar-nav-list">
              <li class="nav-item"><div class="sidebar-hr"></div></li>
              <li class="nav-item">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[essOpen ? 'sidebar-link--open' : '', essActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('essOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="user-circle" class="sidebar-link-icon" />
                  <span>Self Service</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': essOpen }" />
                </button>
                <div class="sidebar-children" v-show="essOpen">
                  <router-link :to="$url('/hr/my-dashboard')"   class="sidebar-child" @click="closeSidebar">Dashboard</router-link>
                  <router-link :to="$url('/hr/my-attendance')"  class="sidebar-child" @click="closeSidebar">My Attendance</router-link>
                  <router-link :to="$url('/hr/my-leaves')" class="sidebar-child" @click="closeSidebar">Leave</router-link>
                  <router-link :to="$url('/hr/my-loans')"       class="sidebar-child" @click="closeSidebar">Loan</router-link>
                  <router-link :to="$url('/hr/my-assets')"      class="sidebar-child" @click="closeSidebar">My Assets</router-link>
                  <router-link :to="$url('/hr/my-shifts')"      class="sidebar-child" @click="closeSidebar">Shift Schedules</router-link>
                  <router-link :to="$url('/hr/my-payslips')"    class="sidebar-child" @click="closeSidebar">Payslips</router-link>
                  <router-link :to="$url('/hr/my-profile')"     class="sidebar-child" @click="closeSidebar">Employee Profile</router-link>
                  <router-link :to="$url('/hr/my-notifications')"  class="sidebar-child" @click="closeSidebar">Notifications</router-link>
                  <router-link :to="$url('/hr/my-announcements')" class="sidebar-child" @click="closeSidebar">Announcements</router-link>
                  <router-link :to="$url('/hr/my-documents')"    class="sidebar-child" @click="closeSidebar">Documents</router-link>
                  <router-link :to="$url('/hr/my-interviews')"  class="sidebar-child" @click="closeSidebar">My Interviews</router-link>
                  <button
                    class="sidebar-sub-toggle"
                    :class="essTrainingOpen ? 'sidebar-sub-toggle--open' : ''"
                    @click.stop="essTrainingOpen = !essTrainingOpen"
                    type="button"
                  >
                    <span>Training</span>
                    <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': essTrainingOpen }" />
                  </button>
                  <div class="sidebar-grandchildren" v-show="essTrainingOpen">
                    <router-link :to="$url('/hr/training/my-training')"  class="sidebar-grandchild" @click="closeSidebar">My Training</router-link>
                    <router-link :to="$url('/hr/training/certificates')" class="sidebar-grandchild" @click="closeSidebar">My Certificates</router-link>
                  </div>
                  <template v-if="(!planCan || planCan.visitors_access) && permission?.visitor_self_access">
                    <button
                      class="sidebar-sub-toggle"
                      :class="essVisitorsOpen ? 'sidebar-sub-toggle--open' : ''"
                      @click.stop="essVisitorsOpen = !essVisitorsOpen"
                      type="button"
                    >
                      <span>Visitors</span>
                      <font-awesome-icon icon="angle-right" class="sub-toggle-chevron" :class="{ 'sub-toggle-chevron--open': essVisitorsOpen }" />
                    </button>
                    <div class="sidebar-grandchildren" v-show="essVisitorsOpen">
                      <router-link :to="$url('/hr/visitors/pre-register?ess=1')" class="sidebar-grandchild" @click="closeSidebar">Pre-Register Visitor</router-link>
                      <router-link :to="$url('/hr/visitors/upcoming')"     class="sidebar-grandchild" @click="closeSidebar">My Visitors</router-link>
                    </div>
                  </template>
                  <router-link v-if="permission?.exit_self_access" :to="$url('/hr/my-exit')" class="sidebar-child" @click="closeSidebar">My Exit</router-link>
                  <router-link :to="$url('/hr/chat')" class="sidebar-child" @click="closeSidebar">Chat</router-link>
                  <router-link :to="$url('/workflow-approvals')" class="sidebar-child" @click="closeSidebar">Pending Approvals</router-link>
                </div>
              </li>
              <!-- Settings accordion -->
              <li class="nav-item">
                <button
                  class="sidebar-link sidebar-link--toggle"
                  :class="[settingOpen ? 'sidebar-link--open' : '', settingActive ? 'sidebar-link--active' : '']"
                  @click="toggleMenu('settingOpen')"
                  type="button"
                >
                  <font-awesome-icon icon="boxes" class="sidebar-link-icon" />
                  <span>Setting</span>
                  <font-awesome-icon icon="angle-right" class="toggle-chevron" :class="{ 'toggle-chevron--open': settingOpen }" />
                </button>
                <div class="sidebar-children" v-show="settingOpen">
                  <!-- FIX: use <a> not <router-link> — router-link requires a `to` prop -->
                  <a class="sidebar-child" style="cursor:pointer; color:inherit;" @click="openModal('email'); closeSidebar()">
                    Change Email
                  </a>
                  <a class="sidebar-child" style="cursor:pointer; color:inherit;" @click="openModal('password'); closeSidebar()">
                    Change Password
                  </a>
                </div>
              </li>

              <!-- Support Tickets -->
              <li class="nav-item" v-if="permission?.view_ticket || permission?.manage_ticket">
                <router-link :to="$url('/support/tickets')" class="sidebar-link" @click="closeSidebar">
                  <font-awesome-icon icon="ticket-alt" class="sidebar-link-icon" />
                  <span>Support</span>
                </router-link>
              </li>

              <li class="nav-item"><div class="sidebar-hr"></div></li>

              <li class="nav-item">
                <a data-bs-toggle="modal" data-bs-target="#logoutmodal" class="sidebar-link sidebar-link--logout" style="cursor:pointer;" @click="closeSidebar">
                  <font-awesome-icon icon="sign-out-alt" class="sidebar-link-icon" />
                  <span>Logout</span>
                </a>
              </li>
            </ul>

            <!-- Help Card -->
            <div class="help-card mx-2 mb-4 mt-3">
              <div class="help-icon">?</div>
              <div class="help-title">Need help?</div>
              <div class="help-sub">Quickly contact us on:</div>
              <div class="help-contacts">
                <a :href="'tel:' + details.tel"      class="help-contact-link">{{ details.tel }}</a>
                <a :href="'tel:' + details.tel2"     class="help-contact-link">{{ details.tel2 }}</a>
                <a :href="'mailto:' + details.email" class="help-contact-link">{{ details.email }}</a>
              </div>
              <router-link to="/contact" class="help-contact-btn">Contact Us</router-link>
            </div>

          </div>
        </div>

        <!-- MAIN CONTENT -->
        <div class="col py-3" style="background:#edf0f5; overflow-x:hidden;">
          <router-view></router-view>
        </div>

      </div>
    </div>

    <!-- ── Logout Modal (Bootstrap) ── -->
    <div class="modal fade" id="logoutmodal" tabindex="-1" aria-labelledby="logoutModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content logout-modal">
          <button type="button" class="modal-dismiss" data-bs-dismiss="modal" aria-label="Close" :disabled="logoutLoading">
            <font-awesome-icon icon="times" />
          </button>
          <div class="modal-body logout-body">
            <div class="logout-icon-wrap">
              <font-awesome-icon icon="sign-out-alt" class="logout-icon" />
            </div>
            <h5 class="logout-title">Sign Out</h5>
            <p class="logout-sub">Are you sure you want to sign out of your account?</p>
          </div>
          <div class="logout-footer">
            <button type="button" class="btn-cancel" data-bs-dismiss="modal" :disabled="logoutLoading">Cancel</button>
            <button type="button" class="btn-confirm" :disabled="logoutLoading" @click="logout">
              <font-awesome-icon v-if="logoutLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="sign-out-alt" />
              {{ logoutLoading ? 'Signing out...' : 'Yes, Sign Out' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════
         CLOCK IN/OUT CONFIRM MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: clockModal }" @click.self="!clockSubmitting && (clockModal = false)">
      <div class="modal-box clock-modal-box">

        <!-- Header -->
        <div class="clock-modal-hd" :class="clockModalMode === 'in' ? 'clock-modal-hd--in' : 'clock-modal-hd--out'">
          <div class="clock-modal-hd-inner">
            <div class="clock-modal-hd-icon">
              <font-awesome-icon :icon="clockModalMode === 'in' ? 'sign-in-alt' : 'sign-out-alt'" />
            </div>
            <div>
              <p class="clock-modal-title">{{ clockModalMode === 'in' ? 'Clock In' : 'Clock Out' }}</p>
              <p class="clock-modal-sub">{{ new Date().toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }) }}</p>
            </div>
          </div>
          <button class="clock-modal-close" :disabled="clockSubmitting" @click="clockModal = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <!-- Body -->
        <div class="clock-modal-bd">

          <!-- No-shift / calculated employee: skip location entirely -->
          <template v-if="noShiftClock">
            <div class="clock-geo-status clock-geo-status--match">
              <div class="clock-geo-status-icon clock-geo-status-icon--green">
                <font-awesome-icon icon="check-circle" />
              </div>
              <div>
                <p class="clock-geo-status-title">Ready to {{ clockModalMode === 'in' ? 'Clock In' : 'Clock Out' }}</p>
                <p class="clock-geo-status-addr">No shift assigned — location check not required.</p>
              </div>
            </div>

            <!-- Achievements on clock out -->
            <div v-if="clockModalMode === 'out'" class="clock-reason-wrap">
              <label class="clock-reason-label">
                Today's Achievements
                <span class="clock-reason-hint">Optional — share what you accomplished</span>
              </label>
              <textarea
                class="clock-reason-input"
                rows="4"
                placeholder="e.g. Completed payroll review, resolved 5 support tickets, attended team standup..."
                v-model="clockAchievements"
                :disabled="clockSubmitting"
              ></textarea>
            </div>
          </template>

          <!-- Standard flow with location check -->
          <template v-else>

            <!-- Location loading -->
            <div v-if="clockGeoLoading" class="clock-geo-loading">
              <div class="clock-geo-spinner"></div>
              <p class="clock-geo-loading-text">Detecting your location...</p>
            </div>

            <template v-else>

              <!-- Location matched -->
              <div v-if="clockGeoMatch === true" class="clock-geo-status clock-geo-status--match">
                <div class="clock-geo-status-icon clock-geo-status-icon--green">
                  <font-awesome-icon icon="check-circle" />
                </div>
                <div>
                  <p class="clock-geo-status-title">Location Verified</p>
                  <p class="clock-geo-status-addr">{{ clockGeoAddress }}</p>
                </div>
              </div>

              <!-- Location does not match -->
              <div v-else-if="clockGeoMatch === false" class="clock-geo-status clock-geo-status--nomatch">
                <div class="clock-geo-status-icon clock-geo-status-icon--amber">
                  <font-awesome-icon icon="exclamation-triangle" />
                </div>
                <div>
                  <p class="clock-geo-status-title">Location Mismatch</p>
                  <p class="clock-geo-status-addr">{{ clockGeoAddress || 'Could not verify your location against the office address.' }}</p>
                </div>
              </div>

              <!-- Location error / permission denied -->
              <div v-else class="clock-geo-status clock-geo-status--error">
                <div class="clock-geo-status-icon clock-geo-status-icon--red">
                  <font-awesome-icon icon="map-marker-alt" />
                </div>
                <div>
                  <p class="clock-geo-status-title">Location Unavailable</p>
                  <p class="clock-geo-status-addr">{{ clockGeoError || 'Enable location access to verify your attendance.' }}</p>
                </div>
              </div>

              <!-- Reason field — shown when location does not match or is unavailable -->
              <div v-if="clockGeoMatch !== true" class="clock-reason-wrap">
                <label class="clock-reason-label">
                  Location Reason <span class="req">*</span>
                  <span class="clock-reason-hint">Required when location cannot be verified</span>
                </label>
                <textarea
                  class="clock-reason-input"
                  rows="3"
                  placeholder="e.g. Working from home, client site visit..."
                  v-model="clockOffReason"
                  :disabled="clockSubmitting"
                ></textarea>
              </div>

              <!-- Early clock-in reason -->
              <div v-if="isEarlyClockIn && clockModalMode === 'in'" class="clock-reason-wrap">
                <label class="clock-reason-label">
                  Reason for Early Clock-in <span class="req">*</span>
                  <span class="clock-reason-hint">Your shift starts at {{ fmtHM(todayShift?.start_time) }}</span>
                </label>
                <textarea
                  class="clock-reason-input"
                  rows="3"
                  placeholder="e.g. Arriving early to prepare for the shift..."
                  v-model="clockEarlyInReason"
                  :disabled="clockSubmitting"
                ></textarea>
              </div>

              <!-- Early / late clock-out reason -->
              <div v-if="isEarlyOrLateClockOut && clockModalMode === 'out'" class="clock-reason-wrap">
                <label class="clock-reason-label">
                  Reason for {{ clockoutType === 'early_clockout' ? 'Early' : 'Late' }} Clock-out <span class="req">*</span>
                  <span class="clock-reason-hint">Shift ends at {{ fmtHM(todayShift?.end_time) }}</span>
                </label>
                <textarea
                  class="clock-reason-input"
                  rows="3"
                  placeholder="e.g. All tasks completed, manager approved early finish..."
                  v-model="clockoutReason"
                  :disabled="clockSubmitting"
                ></textarea>
              </div>

            </template>
          </template>
        </div>

        <!-- Footer -->
        <div class="clock-modal-ft">
          <button class="btn-ghost" :disabled="clockSubmitting || clockGeoLoading" @click="clockModal = false">
            Cancel
          </button>
          <button
            class="btn-clock-confirm"
            :class="clockModalMode === 'in' ? 'btn-clock-confirm--in' : 'btn-clock-confirm--out'"
            :disabled="clockSubmitting || clockGeoLoading
              || (!noShiftClock && clockGeoMatch !== true && !clockOffReason.trim())
              || (isEarlyClockIn && clockModalMode === 'in' && clockEarlyInReason.trim().length < 5)
              || (isEarlyOrLateClockOut && clockModalMode === 'out' && clockoutReason.trim().length < 5)"
            @click="confirmClockAction"
          >
            <span v-if="clockSubmitting" class="clockin-spinner"></span>
            {{ clockSubmitting ? 'Processing...' : (clockModalMode === 'in' ? 'Confirm Clock In' : 'Confirm Clock Out') }}
          </button>
        </div>

      </div>
    </div>

    <!-- ── Change Password Modal ── -->
    <div class="modal-overlay" :class="{ open: password_active }" @click.self="closeModal('password')">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Change Password</span>
          <button class="modal-close-btn" @click="closeModal('password')">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div v-if="validationPassErrors.length" class="error-box">
          <div v-for="(err, i) in flatErrors(validationPassErrors)" :key="i" class="error-item">
            <font-awesome-icon icon="exclamation-circle" class="me-1" /> {{ err }}
          </div>
        </div>

        <form class="modal-body-styled" @submit.prevent="changePassword">
          <div class="form-group-styled">
            <label class="form-label-styled">Current Password</label>
            <div class="pw-wrap">
              <input
                v-model="password"
                :type="showCurrentPassword ? 'password' : 'text'"
                class="form-input-styled"
                placeholder="Enter current password"
              />
              <button type="button" class="pw-toggle" @click="showCurrentPassword = !showCurrentPassword">
                <font-awesome-icon :icon="showCurrentPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">New Password</label>
            <div class="pw-wrap">
              <input
                v-model="new_password"
                :type="showPassword ? 'password' : 'text'"
                class="form-input-styled"
                placeholder="Enter new password"
              />
              <button type="button" class="pw-toggle" @click="showPassword = !showPassword">
                <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
          </div>
          <div class="form-group-styled" style="margin-bottom:0;">
            <label class="form-label-styled">Confirm New Password</label>
            <div class="pw-wrap">
              <input
                v-model="password_confirmation"
                :type="showConfirmPassword ? 'password' : 'text'"
                class="form-input-styled"
                placeholder="Confirm new password"
              />
              <button type="button" class="pw-toggle" @click="showConfirmPassword = !showConfirmPassword">
                <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
          </div>
        </form>

        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="closeModal('password')">Cancel</button>
          <button class="btn-primary-action" :disabled="passloading" @click="changePassword">
            <font-awesome-icon v-if="passloading" icon="spinner" spin class="me-1" />
            {{ passloading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Change Email Modal ── -->
    <div class="modal-overlay" :class="{ open: email_active }" @click.self="closeModal('email')">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Change Email</span>
          <button class="modal-close-btn" @click="closeModal('email')">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div v-if="validationErrors && validationErrors.length" class="error-box">
          <div v-for="(err, i) in flatErrors(validationErrors)" :key="i" class="error-item">
            <font-awesome-icon icon="exclamation-circle" class="me-1" /> {{ err }}
          </div>
        </div>

        <form class="modal-body-styled" @submit.prevent="changeEmail">
          <div class="form-group-styled">
            <label class="form-label-styled">Current Email</label>
            <input v-model="email" type="email" class="form-input-styled" placeholder="Enter current email" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">New Email</label>
            <input v-model="new_email" type="email" class="form-input-styled" placeholder="Enter new email" />
          </div>
          <div class="form-group-styled" style="margin-bottom:0;">
            <label class="form-label-styled">Confirm New Email</label>
            <input v-model="email_confirmation" type="email" class="form-input-styled" placeholder="Confirm new email" />
          </div>
        </form>

        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="closeModal('email')">Cancel</button>
          <button class="btn-primary-action" :disabled="loading" @click="changeEmail">
            <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
            {{ loading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- FIX: Modal backdrop — required for Vue modals to dim the background -->
    <div v-if="password_active || email_active || clockModal" class="modal-backdrop-custom" @click="closeAllModals"></div>

    <!-- ── Lock Screen ── -->
    <transition name="lock-fade">
      <div v-if="isLocked" class="lock-overlay" @keydown.enter="unlockSession">
        <div class="lock-card">
          <div class="lock-avatar">{{ userInitials }}</div>
          <h5 class="lock-name">{{ user && user.first_name ? `${user.first_name} ${user.last_name}` : '' }}</h5>
          <p class="lock-subtitle">Your session was locked due to inactivity</p>

          <div class="lock-input-wrap">
            <input
              ref="lockInput"
              v-model="lockPassword"
              :type="showLockPassword ? 'text' : 'password'"
              class="lock-input"
              :class="{ 'lock-input--error': lockError }"
              placeholder="Enter your password"
              :disabled="lockLoading"
              @input="lockError = ''"
            />
            <button type="button" class="lock-pw-toggle" @click="showLockPassword = !showLockPassword" tabindex="-1">
              <font-awesome-icon :icon="showLockPassword ? 'eye-slash' : 'eye'" />
            </button>
          </div>

          <p v-if="lockError" class="lock-error">
            <font-awesome-icon icon="exclamation-circle" class="me-1" />{{ lockError }}
          </p>

          <button class="lock-btn" :disabled="lockLoading || !lockPassword" @click="unlockSession">
            <font-awesome-icon v-if="lockLoading" icon="spinner" spin class="me-1" />
            {{ lockLoading ? 'Verifying…' : 'Unlock' }}
          </button>

          <a class="lock-signout" data-bs-toggle="modal" data-bs-target="#logoutmodal" @click="isLocked = false">
            Sign out instead
          </a>
        </div>
      </div>
    </transition>

    <!-- ── Floating Chat Button ── -->
    <router-link
      v-if="user && $route.name !== 'HR Chat'"
      :to="$url('/hr/chat')"
      class="chat-fab"
      :title="`Chat${chatFabCount > 0 ? ' · ' + chatFabCount + ' unread' : ''}`"
      @click="chatFabCount = 0"
    >
      <font-awesome-icon icon="comment-dots" />
      <transition name="chat-fab-pop">
        <span v-if="chatFabCount > 0" class="chat-fab-badge">{{ chatFabCount > 99 ? '99+' : chatFabCount }}</span>
      </transition>
    </router-link>

  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import hostName from '../main';
import NotificationBell from '../components/NotificationBell.vue';
import ChatBell from '../components/ChatBell.vue';

export default {
  name: 'TopNav',
  components: { NotificationBell, ChatBell },

  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  data() {
    const store = useStore();
    return {
      user:           '',
      details:        {},
      business:       {},
      permission:     store.state.getCurrentUserPermission || null,
      companies:      [],
      active_company: {},
      store:          store,
      route:          useRouter(),
      toast:          useToast(),
      logoutLoading:  false,
      freemium:       true,

      // Clock in/out
      clockedIn:          false,
      clockLoading:       false,
      clockInTime:        null,
      elapsedTime:        '00:00:00',
      clockTimer:         null,
      noShiftClock:       false,  // no shifts at all — skip location
      todayShift:         null,   // today's active shift for window enforcement
      clockWindowOpen:    true,   // false when more than 30min before shift start
      clockWindowOpensAt: '',     // display time "09:30"
      clockWindowTimer:   null,

      // Clock-in confirm modal
      clockModal:              false,
      clockModalMode:          'in',
      clockGeoLoading:         true,
      clockGeoMatch:           null,
      clockGeoAddress:         '',
      clockGeoCoords:          null,
      clockGeoError:           '',
      clockOffReason:          '',
      clockAchievements:       '',
      clockSubmitting:         false,
      clockEarlyInReason:      '',   // reason when clocking in before shift start
      clockoutReason:          '',   // reason when clocking out early or late
      clockoutType:            '',   // 'early_clockout' | 'late_clockout'
      isEarlyClockIn:          false,
      isEarlyOrLateClockOut:   false,
      url:            hostName,
      dropdownOpen:    false,
      sidebarOpen:     false,
      inventoryOpen:    false,
      accountingOpen:   false,
      procurementOpen:      false,
      crmOpen:              false,
      projectModuleOpen:    false,
      settingOpen:     false,
      hrOpen:          false,
      essOpen:         false,
      hrEmployeesOpen:     false,
      hrPayrollOpen:       false,
      hrAttendanceOpen:    false,
      hrLeaveOpen:         false,
      hrSettingsOpen:      false,
      hrRecruitmentOpen:   false,
      hrPmsOpen:           false,
      hrTrainingOpen:      false,
      hrExitsOpen:         false,
      visitorsOpen:        false,
      developerOpen:       false,
      essVisitorsOpen:     false,
      essTrainingOpen:     false,
      loader:          null,

      // Change password
      passloading:          false,
      password:             '',
      new_password:         '',
      password_confirmation:'',
      password_active:      false,
      showCurrentPassword:  true,
      showPassword:         true,
      showConfirmPassword:  true,
      validationPassErrors: [],

      // Change email
      loading:           false,
      email:             '',
      new_email:         '',
      email_confirmation:'',
      email_active:      false,
      validationErrors:  [],

      // Floating chat button
      chatFabCount: 0,
      chatFabTimer: null,

      // Lock screen
      isLocked:         false,
      lockPassword:     '',
      lockLoading:      false,
      lockError:        '',
      showLockPassword: false,
      inactivityTimer:  null,
    };
  },

  computed: {
    hasAdminAccess() {
      const p = this.permission;
      return !!(p?.administration_module || p?.view_store_management ||
        p?.inventory_mgt_module || p?.sales_module || p?.purchase_module ||
        p?.accounting_module || p?.payroll_module || p?.hr_module ||
        p?.invoice_module || p?.billing_module || p?.report_module);
    },

    logoDestination() {
      return this.hasAdminAccess
        ? '/' + this.$route.params.companyCode
        : this.$url('/hr/my-dashboard');
    },

    userInitials() {
      if (!this.user || !this.user.first_name) return '?';
      return ((this.user.first_name[0] || '') + (this.user.last_name[0] || '')).toUpperCase();
    },

    inventoryActive() {
      const n = this.$router.currentRoute.value.name;
      return [
        'Product Location','All Products','Stock Transfer','Stock Adjustment','Create Product',
        'View Product','Expired Products','Create Stock Transfer','Create Stock Adjustment','Edit Product',
        'Print Stock Adjustment','Print Stock Transfer','View Stock Transfer','View Stock Adjustment',
        'All Internal Issues','Create Internal Issue','View Internal Issue',
        'Bulk Product Upload','Debtor','View Debtor Receipt','Creditor','View Creditor Receipt',
        'All Sales','Make Sales','View Sales Receipt','View Sales','All Purchase','Make Purchase',
        'Notification','View Purchase Receipt','Purchase View','Manage Store',
        'All Customers','All Suppliers','All Payments','Tax',
      ].includes(n) || (n && (n.includes('Report') || n.includes('Reports') || n.includes('Inventory')));
    },

    accountingActive() {
      return [
        'Transactions','Manage Ledgers','Transaction Detail','Upload Transaction','Manage Expenses',
        'Create Expense','Expense Detail','Expense Category','Manage Journals','New Journal',
        'Accounting Dashboard','Journal Entries','Balance Sheet Report','Trial Balance Report',
        'Income Statement Report','Chart of Account','Vendor Payments','Payment Vouchers',
        'Bills','Petty Cash','Fiscal Years','Bank Accounts Setup','Business Wallet',
      ].includes(this.$router.currentRoute.value.name);
    },

    hrEmployeesActive() {
      return ['Employees Dashboard','Manage Employees','Create Employee','Employee Details','Manage Employees Invites']
        .includes(this.$router.currentRoute.value.name);
    },
    hrPayrollActive() {
      return ['Payroll Dashboard','Payroll Periods','Payroll History','Create Payroll','Payroll Details','Payslip',
              'Payroll Additional Earning','Payroll Additional Deduction','Payroll Statutory Deductions']
        .includes(this.$router.currentRoute.value.name);
    },
    hrAttendanceActive() {
      return ['HR Attendance','HR Shifts','HR Rota'].includes(this.$router.currentRoute.value.name);
    },
    hrLeaveActive() {
      return ['HR Leave','HR Leave Balances'].includes(this.$router.currentRoute.value.name);
    },
    hrSettingsActive() {
      return ['Manage Department','Manage Department Units','Manage Positions','Manage Paygrade','Paygrade Detail',
              'Salary Components','HR Public Holidays','HR Clients']
        .includes(this.$router.currentRoute.value.name);
    },
    hrLoansActive() {
      return ['HR Loans'].includes(this.$router.currentRoute.value.name);
    },
    assetModuleActive() {
      return ['Assets'].includes(this.$router.currentRoute.value.name);
    },
    procurementActive() {
      return ['Procurement Dashboard', 'Vendor Directory', 'Vendor Profile', 'Vendor Categories', 'Purchase Requisitions', 'Create PR', 'PR Detail', 'Edit PR',
              'Purchase Orders', 'Create PO', 'PO Detail', 'Edit PO',

              'Sourcing Events', 'Create Sourcing Event', 'Sourcing Event Detail', 'Edit Sourcing Event']
        .includes(this.$router.currentRoute.value.name);
    },
    crmActive() {
      return ['CRM Dashboard', 'CRM Leads', 'CRM Lead Detail', 'CRM Contacts', 'CRM Contact Detail',
              'CRM Accounts', 'CRM Account Detail', 'CRM Opportunities', 'CRM Opportunity Detail',
              'CRM Activities', 'CRM Segments', 'CRM Campaigns', 'CRM Campaign Detail',
              'CRM Journeys', 'CRM Journey Detail', 'CRM Cases', 'CRM Case Detail',
              'CRM Loyalty', 'CRM Surveys', 'CRM Survey Detail']
        .includes(this.$router.currentRoute.value.name);
    },
    projectModuleActive() {
      return ['Project Module', 'Project Dashboard', 'Project Module Detail', 'Budget Detail', 'Timesheets']
        .includes(this.$router.currentRoute.value.name);
    },
    hrRecruitmentActive() {
      return ['HR Recruitment'].includes(this.$router.currentRoute.value.name);
    },
    hrPmsActive() {
      return ['PMS Cycles', 'PMS Cycle Detail', 'PMS Goals', 'PMS KPI Library', 'PMS Competencies',
              'PMS Reviews', 'PMS Review Form', 'PMS Bonus Engine', 'PMS Payroll Sync', 'PMS Reports']
        .includes(this.$router.currentRoute.value.name);
    },
    hrTrainingActive() {
      return ['Training Dashboard', 'Training Courses', 'My Training']
        .includes(this.$router.currentRoute.value.name);
    },
    visitorsActive() {
      const name = this.$router.currentRoute.value.name
      const isEss = !!this.$router.currentRoute.value.query?.ess
      if (isEss && ['Pre-Register Visitor', 'Upcoming Visitors'].includes(name)) return false
      return ['Visitor Dashboard', 'Pre-Register Visitor', 'Upcoming Visitors', 'Visitor Check-In',
              'Active Visitors', 'Visitor History', 'Visitor Settings', 'Visitor Directory',
              'Visitor Types', 'Visitor Sites', 'Visitor Watchlist', 'Visitor Documents',
              'Visitor Approvals', 'Visitor Roll-Call', 'Visitor Evacuation',
              'Visitor Reports', 'Visitor Audit Trail', 'Visitor DSR',
              'Visitor Group Visits'].includes(name)
    },
    developerActive() {
      return ['Developer API Keys', 'Developer Webhooks', 'Developer Docs']
        .includes(this.$router.currentRoute.value.name);
    },
    exitsActive() {
      return ['Exit Pipeline', 'Exit Cases', 'Initiate Exit', 'Exit Case', 'Exit Settings', 'My Exit']
        .includes(this.$router.currentRoute.value.name);
    },
    hrActive() {
      return this.hrEmployeesActive || this.hrPayrollActive || this.hrAttendanceActive
          || this.hrLeaveActive     || this.hrSettingsActive || this.hrLoansActive
          || this.hrRecruitmentActive || this.hrPmsActive || this.hrTrainingActive
          || this.exitsActive
          || ['HR Documents'].includes(this.$router.currentRoute.value.name);
    },

    essVisitorsActive() {
      const name = this.$router.currentRoute.value.name
      const isEss = !!this.$router.currentRoute.value.query?.ess
      if (!isEss && name === 'Pre-Register Visitor') return false
      return ['Pre-Register Visitor', 'Upcoming Visitors'].includes(name)
    },
    essTrainingActive() {
      return ['My Training','Training Certificates'].includes(this.$router.currentRoute.value.name);
    },
    essActive() {
      return this.essVisitorsActive || this.essTrainingActive || ['My Dashboard','My Attendance','My Leaves','My Loans','My Shifts','My Payslips','My Profile',
              'My Notifications','Manage Workflow Approvals','My Announcements','My Documents','My Assets','My Interviews','My Exit']
        .includes(this.$router.currentRoute.value.name);
    },

    settingActive() {
      return false;
    },

    // Plan feature access — falls back to all-allow when planFeatures is null (trial / not loaded)
    planCan() {
      const features = this.store.state.planFeatures;
      // When null (trial or not loaded), return an object where every key is truthy
      if (!features) return null;
      return features;
    },
  },

  methods: {
    // -- Sidebar -------------------------------------------------------
    toggleSidebar() { this.sidebarOpen = !this.sidebarOpen; },
    toggleMenu(key) {
      const topLevel = [
        'inventoryOpen', 'accountingOpen', 'hrOpen', 'procurementOpen',
        'crmOpen', 'projectModuleOpen', 'visitorsOpen', 'developerOpen', 'essOpen', 'settingOpen',
      ];
      const wasOpen = this[key];
      topLevel.forEach(k => { this[k] = false; });
      // Also collapse all HR sub-menus when switching top-level sections
      if (key !== 'hrOpen') {
        ['hrEmployeesOpen','hrPayrollOpen','hrAttendanceOpen','hrLeaveOpen',
         'hrRecruitmentOpen','hrPmsOpen','hrSettingsOpen','hrTrainingOpen','hrExitsOpen'].forEach(k => { this[k] = false; });
      }
      this[key] = !wasOpen;
    },
    toggleHrMenu(key) {
      const hrSubs = [
        'hrEmployeesOpen','hrPayrollOpen','hrAttendanceOpen','hrLeaveOpen',
        'hrRecruitmentOpen','hrPmsOpen','hrSettingsOpen','hrTrainingOpen','hrExitsOpen',
      ];
      const wasOpen = this[key];
      hrSubs.forEach(k => { this[k] = false; });
      this[key] = !wasOpen;
    },
    closeSidebar()  { this.sidebarOpen = false; },
    closeDropdown() { this.dropdownOpen = false; },

    // -- Auth ----------------------------------------------------------
    logout() {
      this.logoutLoading = true;
      axios.get('/logout')
        .then(res => {
          if (res.status === 200) {
            localStorage.clear();
            window.location.href = '/auth/login';
          }
        })
        .catch(err => {
          this.logoutLoading = false;
          this.toast.error(err && err.response && err.response.data && err.response.data.message);
        });
    },

    // -- Data ----------------------------------------------------------
    getCurrentUser() {
      axios.get('/user')
        .then(res => {
          this.user = res.data.data.data;
          this.store.dispatch('getCurrentUser', this.user);
        })
        .catch(err => {
          const status = err.response && err.response.status;
          if (status === 403 || status === 401) this.route.push('/auth/login');
          this.toast.error(err && err.response && err.response.data && err.response.data.message);
        });
    },

    getCurrentUserPermission() {
      this.loader && this.loader.show && this.loader.show();
      axios.get('/user/permissions')
        .then(res => {
          setTimeout(() => this.loader && this.loader.hide && this.loader.hide(), 500);
          const perm = res.data.data;
          if (!perm) {
            this.toast.error('Your role permissions could not be loaded. Contact your administrator.');
            return;
          }
          this.permission = perm;
          this.store.dispatch('getCurrentUserPermission', this.permission);
          if (this.$route.name === 'Dashboard' && !this.permission?.view_dashboard) {
            this.$router.replace(this.$url('/hr/my-dashboard'));
          }
        })
        .catch(err => {
          this.loader && this.loader.hide && this.loader.hide();
          this.toast.error(err && err.response && err.response.data && err.response.data.message);
        });
    },

    fetchCompanies() {
      axios.get('/admin/business/my')
        .then(res => {
          this.companies      = res.data.data.businesses;
          this.active_company = res.data.data.active;
          this.loader && this.loader.hide && this.loader.hide();
        })
        .catch(err => {
          this.loader && this.loader.hide && this.loader.hide();
          this.toast.error(err && err.response && err.response.data && err.response.data.message);
        });
    },

    changeCompany(id) {
      axios.get('/admin/business/switch/' + id)
        .then(res => {
          if (res.data && res.data.status === false) {
            this.loader && this.loader.hide && this.loader.hide();
            this.toast.error(res.data.message || 'Failed to switch business');
          } else {
            location.reload();
          }
        })
        .catch(err => {
          this.loader && this.loader.hide && this.loader.hide();
          this.toast.error(err && err.response && err.response.data && err.response.data.message);
        });
    },

    // -- Change Password -----------------------------------------------
    async changePassword() {
      this.passloading = true;
      try {
        const { data } = await axios.post('/user/change-password', {
          password:              this.password,
          new_password:          this.new_password,
          password_confirmation: this.password_confirmation,
        });
        this.toast.success(data.message);
        this.password_active = false;
        this.clearPassFields();
        localStorage.clear();
        this.route.push('/auth/login');
      } catch (err) {
        const status  = err.response && err.response.status;
        const message = err.response && err.response.data && err.response.data.message;
        if (status === 422) {
          this.toast.warning('Invalid inputs — please recheck.');
          if (typeof message === 'string')       this.validationPassErrors = [message];
          else if (Array.isArray(message))       this.validationPassErrors = message.flat();
          else if (message && typeof message === 'object') this.validationPassErrors = Object.values(message).flat();
          else                                   this.validationPassErrors = ['Invalid inputs — please recheck.'];
        } else if (message) {
          this.toast.error(message);
        } else {
          this.toast.error('An error occurred. Please try again.');
        }
      } finally {
        this.passloading = false;
      }
    },

    clearPassFields() {
      this.password = this.new_password = this.password_confirmation = '';
      this.validationPassErrors = [];
    },

    // -- Change Email --------------------------------------------------
    async changeEmail() {
      this.loading = true;
      try {
        const { data } = await axios.post('/user/change-email-web', {
          email:              this.email,
          new_email:          this.new_email,
          email_confirmation: this.email_confirmation,
        });
        this.toast.success(data.message);
        this.email_active = false;
        this.clearEmailFields();
        localStorage.clear();
        this.route.push('/auth/login');
      } catch (err) {
        const status  = err.response && err.response.status;
        const message = err.response && err.response.data && err.response.data.message;
        if (status === 422) {
          this.toast.warning('Invalid inputs — please recheck.');
          this.validationErrors = Array.isArray(message) ? message.flat()
            : message && typeof message === 'object' ? Object.values(message).flat()
            : [message || 'Validation error.'];
        } else {
          this.toast.error('An error occurred. Please try again.');
        }
      } finally {
        this.loading = false;
      }
    },

    clearEmailFields() {
      this.email = this.new_email = this.email_confirmation = '';
      this.validationErrors = [];
    },

    // -- Modal helpers -------------------------------------------------
    openModal(type) {
      if (type === 'password') this.password_active = true;
      else                     this.email_active    = true;
    },

    closeModal(type) {
      if (type === 'password') { this.password_active = false; this.clearPassFields(); }
      else                     { this.email_active    = false; this.clearEmailFields(); }
    },

    closeAllModals() {
      this.password_active = false;
      this.email_active    = false;
      this.clearPassFields();
      this.clearEmailFields();
    },

    // -- Flat error helper ---------------------------------------------
    flatErrors(errors) {
      if (!errors) return [];
      if (typeof errors === 'string') return [errors];
      if (Array.isArray(errors)) return errors.flat();
      if (typeof errors === 'object') return Object.values(errors).flat();
      return [];
    },

    // -- Clock In / Out -----------------------------------------------
    fetchClockStatus() {
      const employeeId = this.store?.state?.getLoggedInUser?.employee_id;
      if (!employeeId) return;
      axios.get('/hr/attendance/today-status', { params: { employee_id: employeeId } })
        .then(({ data }) => {
          const record = data.data || {};
          this.clockedIn     = !!(record.clock_in && !record.clock_out);
          this.clockInTime   = record.clock_in ? new Date(record.clock_in) : null;
          this.noShiftClock  = record.no_shift_clock ?? false;
          this.todayShift    = record.today_shift ?? null;
          this.updateShiftWindow();
          if (this.clockedIn && this.clockInTime) this.startElapsedTimer();
        })
        .catch(() => {});
    },

    openClockModal() {
      this.clockModalMode      = this.clockedIn ? 'out' : 'in';
      this.clockOffReason      = '';
      this.clockAchievements   = '';
      this.clockEarlyInReason  = '';
      this.clockoutReason      = '';
      this.clockGeoCoords      = null;
      this.clockGeoError       = '';
      this.clockModal          = true;

      // Compute early/late flags at the moment the modal opens
      if (!this.noShiftClock && this.todayShift) {
        const [sh, sm] = (this.todayShift.start_time || '00:00').split(':');
        const shiftStart = new Date(); shiftStart.setHours(+sh, +sm, 0, 0);
        this.isEarlyClockIn = !this.clockedIn && new Date() < shiftStart;

        const [eh, em] = (this.todayShift.end_time || '23:59').split(':');
        const shiftEnd = new Date(); shiftEnd.setHours(+eh, +em, 0, 0);
        const earlyLimit = new Date(shiftEnd.getTime() - 15 * 60000);
        const now = new Date();
        this.isEarlyOrLateClockOut = this.clockedIn && (now < earlyLimit || now > shiftEnd);
        this.clockoutType = this.clockedIn
          ? (now < shiftEnd ? 'early_clockout' : 'late_clockout')
          : '';
      } else {
        this.isEarlyClockIn        = false;
        this.isEarlyOrLateClockOut = false;
        this.clockoutType          = '';
      }

      if (this.noShiftClock) {
        this.clockGeoLoading = false;
        this.clockGeoMatch   = null;
        this.clockGeoAddress = '';
      } else {
        this.clockGeoLoading = true;
        this.clockGeoMatch   = null;
        this.clockGeoAddress = '';
        this.detectLocation();
      }
    },

    detectLocation() {
      if (!navigator.geolocation) {
        this.clockGeoError   = 'Geolocation is not supported by your browser.';
        this.clockGeoLoading = false;
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          this.clockGeoCoords = { lat, lng };
          // Reverse geocode via free API
          axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
            .then(({ data }) => {
              this.clockGeoAddress = data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
            })
            .catch(() => {
              this.clockGeoAddress = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
            })
            .finally(() => {
              // Verify against office location stored on the backend
              axios.post('/hr/attendance/verify-location', { lat, lng })
                .then(({ data }) => {
                  this.clockGeoMatch = data.data?.match === true || data.data?.verified === true;
                })
                .catch(() => {
                  // If endpoint doesn't exist yet, treat as unverified (not a hard error)
                  this.clockGeoMatch = false;
                })
                .finally(() => { this.clockGeoLoading = false; });
            });
        },
        (err) => {
          const msgs = {
            1: 'Location access denied. Please enable it in your browser settings.',
            2: 'Location unavailable. Please try again.',
            3: 'Location request timed out.',
          };
          this.clockGeoError   = msgs[err.code] || 'Could not get your location.';
          this.clockGeoLoading = false;
        },
        { timeout: 10000, maximumAge: 60000 }
      );
    },

    confirmClockAction() {
      const employeeId = this.store?.state?.getLoggedInUser?.employee_id;
      if (!employeeId) {
        this.toast.error('No employee profile linked to your account.');
        return;
      }
      if (!this.noShiftClock && this.clockGeoMatch !== true && !this.clockOffReason.trim()) {
        this.toast.error('Please provide a reason for the location mismatch.');
        return;
      }
      this.clockSubmitting = true;
      const endpoint = this.clockedIn ? '/hr/attendance/clock-out' : '/hr/attendance/clock-in';
      const payload  = {
        employee_id: employeeId,
        latitude:    this.noShiftClock ? null : (this.clockGeoCoords?.lat ?? null),
        longitude:   this.noShiftClock ? null : (this.clockGeoCoords?.lng ?? null),
      };
      if (!this.noShiftClock && this.clockGeoMatch !== true && this.clockOffReason.trim()) {
        payload.bypass_location_check  = true;
        payload.location_bypass_reason = this.clockOffReason.trim();
      }
      if (this.isEarlyClockIn && this.clockEarlyInReason.trim()) {
        payload.early_clockin_reason = this.clockEarlyInReason.trim();
      }
      if (this.isEarlyOrLateClockOut && this.clockoutReason.trim()) {
        payload.early_clockout_reason = this.clockoutReason.trim();
      }
      axios.post(endpoint, payload)
        .then(({ data }) => {
          if (this.clockedIn) {
            this.clockedIn   = false;
            this.clockInTime = null;
            this.stopElapsedTimer();
            this.elapsedTime = '00:00:00';
            this.toast.success(data.message || 'Clocked out successfully.');
          } else {
            this.clockedIn   = true;
            this.clockInTime = new Date();
            this.startElapsedTimer();
            this.toast.success(data.message || 'Clocked in successfully.');
          }
          this.clockModal = false;
        })
        .catch(err => {
          this.toast.error(err.response?.data?.message ?? 'Could not record attendance.');
        })
        .finally(() => { this.clockSubmitting = false; });
    },

    startElapsedTimer() {
      this.stopElapsedTimer();
      this.updateElapsed();
      this.clockTimer = setInterval(this.updateElapsed, 1000);
    },

    stopElapsedTimer() {
      if (this.clockTimer) { clearInterval(this.clockTimer); this.clockTimer = null; }
    },

    updateShiftWindow() {
      if (!this.todayShift || this.noShiftClock || this.clockedIn) {
        this.clockWindowOpen = true;
        return;
      }
      const [h, m] = (this.todayShift.start_time || '00:00').split(':');
      const shiftStart = new Date(); shiftStart.setHours(+h, +m, 0, 0);
      const windowTime = new Date(shiftStart.getTime() - 30 * 60000);
      this.clockWindowOpen    = new Date() >= windowTime;
      this.clockWindowOpensAt = windowTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    fmtHM(t) {
      if (!t) return '';
      const [h, m] = t.split(':');
      const d = new Date(); d.setHours(+h, +m, 0, 0);
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    updateElapsed() {
      if (!this.clockInTime) return;
      const diff = Math.floor((Date.now() - this.clockInTime.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      this.elapsedTime = [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
    },

    // -- Lock screen --------------------------------------------------
    resetInactivityTimer() {
      if (this.isLocked) return;
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = setTimeout(() => {
        this.isLocked     = true;
        this.lockPassword = '';
        this.lockError    = '';
        this.$nextTick(() => this.$refs.lockInput?.focus());
      }, 10 * 60 * 1000);
    },

    async unlockSession() {
      if (!this.lockPassword || this.lockLoading) return;
      this.lockLoading = true;
      this.lockError   = '';
      try {
        await axios.post('/user/verify-password', { password: this.lockPassword });
        this.isLocked     = false;
        this.lockPassword = '';
        this.resetInactivityTimer();
      } catch (err) {
        this.lockError = err.response?.data?.message || 'Incorrect password';
        this.$nextTick(() => this.$refs.lockInput?.select());
      } finally {
        this.lockLoading = false;
      }
    },

    // -- Floating chat button count -----------------------------------
    async fetchChatFabCount() {
      try {
        const { data } = await axios.get('/hr/chat/unread-count');
        this.chatFabCount = data.data?.count ?? 0;
      } catch { /* silent */ }
    },

    // -- Subscription / page data -------------------------------------
    activeSubscription() {
      axios.get('/subscription/is-trial')
        .then(res => { this.freemium = res.data.data !== true; })
        .catch(() => {});
    },

    loadPlanFeatures() {
      axios.get('/subscription/user/current-active')
        .then(res => {
          const sub = res.data && res.data.data;
          // Trial (status === 0) gets full access — dispatch null so the allow-all fallback applies
          if (sub && sub.status === 0) {
            this.store.dispatch('setPlanFeatures', null);
            return;
          }
          const planDetail = sub && sub.plan_detail;
          this.store.dispatch('setPlanFeatures', planDetail || null);
        })
        .catch(() => {
          // On error, clear stale plan data so sidebar doesn't stay locked
          this.store.dispatch('setPlanFeatures', null);
        });
    },

    pageData() {
      axios.get('/admin/fetchProjectSetup')
        .then(res => { this.details = res.data.data; })
        .catch(() => {});
    },

    userActiveBusiness() {
      axios.get('/admin/business/user/active-business')
        .then(res => { this.business = res.data.data; })
        .catch(() => {});
    },
  },

  created() {
    this.activeSubscription();
    this.loadPlanFeatures();
    this.fetchClockStatus();
    this.getCurrentUserPermission();
    this.getCurrentUser();
    this.userActiveBusiness();
    this.pageData();
    this.fetchCompanies();
    this.fetchChatFabCount();
    this.chatFabTimer = setInterval(this.fetchChatFabCount, 30000);
    // Keep shift window open/closed state current while page is open
    this.clockWindowTimer = setInterval(() => this.updateShiftWindow(), 15000);
  },

  mounted() {
    this.inventoryOpen   = this.inventoryActive;
    this.accountingOpen  = this.accountingActive;
    this.procurementOpen     = this.procurementActive;
    this.crmOpen             = this.crmActive;
    this.projectModuleOpen   = this.projectModuleActive;
    this.hrOpen          = this.hrActive;
    this.essOpen         = this.essActive;
    this.essVisitorsOpen = this.essVisitorsActive;
    this.essTrainingOpen = this.essTrainingActive;
    this.hrEmployeesOpen     = this.hrEmployeesActive;
    this.hrPayrollOpen       = this.hrPayrollActive;
    this.hrAttendanceOpen    = this.hrAttendanceActive;
    this.hrLeaveOpen         = this.hrLeaveActive;
    this.hrSettingsOpen      = this.hrSettingsActive;
    this.hrPmsOpen           = this.hrPmsActive;
    this.hrTrainingOpen      = this.hrTrainingActive;
    this.hrExitsOpen         = this.exitsActive;
    this.visitorsOpen        = this.visitorsActive;
    this.developerOpen       = this.developerActive;

    if (this.$loading) {
      this.loader = this.$loading.show({
        container: this.$refs.formContainer,
        color:  '#004AAD',
        width:  100,
        height: 100,
      });
    }

    // Inactivity lock: start timer and attach activity listeners
    this._activityHandler = () => this.resetInactivityTimer();
    ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'].forEach(ev =>
      document.addEventListener(ev, this._activityHandler, true)
    );
    this.resetInactivityTimer();
  },

  beforeUnmount() {
    this.stopElapsedTimer();
    if (this.clockWindowTimer) clearInterval(this.clockWindowTimer);
    if (this.chatFabTimer)     clearInterval(this.chatFabTimer);
    clearTimeout(this.inactivityTimer);
    if (this._activityHandler) {
      ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'].forEach(ev =>
        document.removeEventListener(ev, this._activityHandler, true)
      );
    }
  },

  watch: {
    $route() {
      this.inventoryOpen   = this.inventoryActive;
      this.accountingOpen  = this.accountingActive;
      this.procurementOpen     = this.procurementActive;
      this.crmOpen             = this.crmActive;
      this.projectModuleOpen   = this.projectModuleActive;
      this.visitorsOpen        = this.visitorsActive || this.visitorsOpen;
      this.developerOpen       = this.developerActive || this.developerOpen;
      if (this.essActive) {
        this.essOpen = true;
        this.essVisitorsOpen = this.essVisitorsActive || this.essVisitorsOpen;
        this.essTrainingOpen = this.essTrainingActive || this.essTrainingOpen;
      }
      if (this.hrActive) {
        this.hrOpen           = true;
        this.hrEmployeesOpen  = this.hrEmployeesActive  || this.hrEmployeesOpen;
        this.hrPayrollOpen    = this.hrPayrollActive    || this.hrPayrollOpen;
        this.hrAttendanceOpen = this.hrAttendanceActive || this.hrAttendanceOpen;
        this.hrLeaveOpen      = this.hrLeaveActive      || this.hrLeaveOpen;
        this.hrSettingsOpen   = this.hrSettingsActive   || this.hrSettingsOpen;
        this.hrPmsOpen        = this.hrPmsActive        || this.hrPmsOpen;
        this.hrTrainingOpen   = this.hrTrainingActive   || this.hrTrainingOpen;
        this.hrExitsOpen      = this.exitsActive        || this.hrExitsOpen;
      }
    },
  },
};
</script>

<style>
body::-webkit-scrollbar, .sidebar_scroll::-webkit-scrollbar { width: 8px; }
body::-webkit-scrollbar-track, .sidebar_scroll::-webkit-scrollbar { background: #051B35; }
body::-webkit-scrollbar-thumb, .sidebar_scroll::-webkit-scrollbar {
  background-color: #DAE7FC;
  border-radius: 20px;
  border: 1px solid #fff;
}
</style>

<style scoped>
/* -- Topnav -------------------------------------------------------------- */
.topnav { position:fixed; top:0; left:0; right:0; z-index:1000; background:#fff; border-bottom:1px solid #e4eaf2; box-shadow:0 1px 8px rgba(0,0,0,.06); height:62px; }
.topnav-inner { display:flex; align-items:center; height:100%; padding:0 20px; gap:16px; }
.topnav-logo  { display:flex; align-items:center; flex-shrink:0; text-decoration:none; }
.logo-img     { max-height:42px; width:auto; object-fit:contain; }
.topnav-pagetitle { font-size:15px; font-weight:700; color:#1a2b45; flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.topnav-right { display:flex; align-items:center; gap:12px; flex-shrink:0; }

/* -- Subscribe ----------------------------------------------------------- */
.btn-subscribe { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:7px 16px; border-radius:8px; text-decoration:none; transition:background .15s; white-space:nowrap; }
.btn-subscribe:hover { background:#003b8e; color:#fff; text-decoration:none; }

/* -- User dropdown ------------------------------------------------------- */
.user-dropdown { position:relative; }
.user-trigger { display:flex; align-items:center; gap:10px; background:transparent; border:1px solid #e4eaf2; border-radius:10px; padding:6px 12px 6px 6px; cursor:pointer; transition:background .15s; }
.user-trigger:hover { background:#f4f7fb; }
.user-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.12); color:#004AAD; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.user-info { display:flex; flex-direction:column; align-items:flex-start; line-height:1.2; }
.user-name { font-size:13px; font-weight:600; color:#1a2b45; white-space:nowrap; }
.user-role { font-size:11px; color:#7a90b0; white-space:nowrap; }
.dropdown-chevron { font-size:11px; color:#7a90b0; transition:transform .2s; margin-left:2px; }
.dropdown-chevron.rotated { transform:rotate(180deg); }
.user-menu { position:absolute; top:calc(100% + 8px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:12px; box-shadow:0 10px 32px rgba(0,0,0,.12); min-width:220px; padding:6px; z-index:999; }
.user-menu-profile { display:flex; align-items:center; gap:12px; padding:10px 10px 12px; }
.user-menu-avatar { width:38px; height:38px; border-radius:50%; background:rgba(0,74,173,.12); color:#004AAD; font-size:13px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.user-menu-name { font-size:13px; font-weight:700; color:#1a2b45; }
.user-menu-role { font-size:12px; color:#7a90b0; margin-top:2px; }
.user-menu-divider { border:none; border-top:1px solid #f0f4f8; margin:2px 0 4px; }
.user-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:9px 12px; border:none; background:transparent; border-radius:8px; font-size:13px; font-weight:500; color:#1a2b45; cursor:pointer; text-align:left; transition:background .12s; }
.user-menu-item:hover { background:#f4f7fb; }
.user-menu-item--danger { color:#e03535; }
.user-menu-item--danger:hover { background:#fff0f0; }
.user-menu-icon { font-size:13px; width:16px; flex-shrink:0; }

/* -- Mobile toggle ------------------------------------------------------- */
.mobile-toggle { display:none; background:transparent; border:1px solid #e4eaf2; border-radius:8px; width:36px; height:36px; align-items:center; justify-content:center; font-size:16px; color:#004AAD; cursor:pointer; transition:background .15s; }
.mobile-toggle:hover { background:#f4f7fb; }

/* -- Sidebar ------------------------------------------------------------- */
.sidebar-fixed { position:fixed; top:62px; left:0; bottom:0; width:240px; background:#051B35; display:flex; flex-direction:column; overflow-y:auto; overflow-x:hidden; z-index:100; scrollbar-width:thin; scrollbar-color:rgba(255,255,255,.1) transparent; }
.sidebar-fixed::-webkit-scrollbar { width:3px; }
.sidebar-fixed::-webkit-scrollbar-thumb { background:rgba(255,255,255,.1); border-radius:3px; }

/* -- Company switcher ---------------------------------------------------- */
.company-switcher-btn { display:flex; align-items:center; gap:8px; width:100%; background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.1); border-radius:8px; color:#fff; font-size:12.5px; font-weight:500; padding:8px 10px; text-align:left; cursor:pointer; transition:background .15s; white-space:nowrap; overflow:hidden; }
.company-switcher-btn:hover  { background:rgba(255,255,255,.11); }
.company-switcher-btn::after { display:none !important; }
.company-green-dot { width:8px; height:8px; border-radius:50%; background:#4cde80; flex-shrink:0; }
.company-name-text { flex:1; overflow:hidden; text-overflow:ellipsis; }
.active-pill { font-size:10px; background:rgba(76,222,128,.12); color:#4cde80; border:1px solid rgba(76,222,128,.25); border-radius:20px; padding:1px 7px; flex-shrink:0; }
.company-dropdown-menu { width:222px; background:#0c2545; border:1px solid rgba(255,255,255,.1); border-radius:8px; padding:4px; box-shadow:0 10px 28px rgba(0,0,0,.4); }
.company-dropdown-item { display:flex !important; align-items:center; gap:9px; border-radius:6px; color:rgba(255,255,255,.75) !important; font-size:13px; padding:7px 10px !important; transition:background .15s; }
.company-dropdown-item:hover { background:rgba(255,255,255,.07) !important; color:#fff !important; }
.company-dot-sm { width:7px; height:7px; border-radius:50%; background:rgba(255,255,255,.2); flex-shrink:0; }
.company-dot-sm.dot-active { background:#4cde80; }

/* -- Sidebar nav --------------------------------------------------------- */
.sidebar-hr { height:1px; background:rgba(255,255,255,.07); margin:8px 12px; }
.sidebar-nav-list { gap:1px; list-style:none; margin:0; padding:0; }
.sidebar-link { display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:7px; color:rgba(255,255,255,.62); text-decoration:none; font-size:13px; font-weight:500; transition:background .15s, color .15s; white-space:nowrap; }
.sidebar-link:hover { background:rgba(255,255,255,.07); color:#fff; }
.sidebar-link--active { background:#004AAD !important; color:#fff !important; box-shadow:0 2px 10px rgba(0,74,173,.35); }
.sidebar-link--toggle { display:flex; width:100%; background:transparent; border:none; cursor:pointer; font-family:inherit; text-align:left; }
.sidebar-link--toggle span { flex:1; }
.sidebar-link--split { display:flex; align-items:center; padding:0; gap:0; }
.sidebar-link-main { display:flex; align-items:center; gap:10px; flex:1; padding:9px 11px; text-decoration:none; color:inherit; font-size:13px; font-weight:500; border-radius:7px 0 0 7px; }
.sidebar-link-main:hover { color:#fff; }
.sidebar-link-chevron { background:transparent; border:none; cursor:pointer; color:inherit; padding:9px 10px 9px 4px; display:flex; align-items:center; border-radius:0 7px 7px 0; }
.sidebar-link-chevron:hover { color:#fff; }
.sidebar-link--logout { color:rgba(255,110,110,.75) !important; }
.sidebar-link--logout:hover { background:rgba(200,50,50,.1) !important; color:#ff8888 !important; }
.sidebar-link-icon { width:15px; text-align:center; flex-shrink:0; opacity:.8; }
.toggle-chevron { font-size:11px; opacity:.4; transition:transform .22s ease; flex-shrink:0; }
.toggle-chevron--open { transform:rotate(90deg); opacity:.85; }
.sidebar-children { display:flex; flex-direction:column; margin-left:14px; padding-left:10px; border-left:1px solid rgba(255,255,255,.08); gap:1px; margin-top:2px; margin-bottom:2px; }
.sidebar-child { display:block; padding:6px 10px; border-radius:6px; color:rgba(255,255,255,.52) !important; font-size:12.5px; text-decoration:none !important; transition:background .15s, color .15s; }
.sidebar-child:hover { background:rgba(255,255,255,.07); color:#fff !important; }
.sidebar-child.router-link-active { color:#fff !important; background:rgba(255,255,255,.06); }

/* ── Level-2 sub-toggle (inside an open sidebar-children) ── */
.sidebar-sub-toggle { display:flex; align-items:center; gap:8px; width:100%; background:none; border:none; cursor:pointer; color:rgba(255,255,255,.52); font-size:12.5px; padding:6px 10px; border-radius:6px; text-align:left; transition:background .15s,color .15s; text-decoration: none; }
.sidebar-sub-toggle:hover,.sidebar-sub-toggle--open { color:#fff; background:rgba(255,255,255,.06); }
.sub-toggle-icon { font-size:11px; flex-shrink:0; }
.sub-toggle-chevron { font-size:10px; margin-left:auto; transition:transform .2s; flex-shrink:0; }
.sub-toggle-chevron--open { transform:rotate(90deg); }

/* ── Level-3 grandchildren ── */
.sidebar-grandchildren { display:flex; flex-direction:column; margin-left:10px; padding-left:10px; border-left:1px solid rgba(255,255,255,.07); gap:1px; }
.sidebar-grandchild { display:block; padding:5px 10px; border-radius:6px; color:rgba(255,255,255,.42) !important; font-size:12px; text-decoration:none !important; transition:background .15s,color .15s; }
.sidebar-grandchild:hover { background:rgba(255,255,255,.06); color:#fff !important; }
.sidebar-grandchild.router-link-active { color:#fff !important; background:rgba(255,255,255,.05); }

/* -- Help card ----------------------------------------------------------- */
.help-card { padding:13px; background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); border-radius:10px; }
.help-icon { width:30px; height:30px; border-radius:50%; background:rgba(0,74,173,.25); border:1px solid rgba(0,74,173,.45); color:#7eb8ff; font-size:14px; font-weight:700; display:flex; align-items:center; justify-content:center; margin-bottom:9px; }
.help-title { color:#fff; font-size:13px; font-weight:600; margin:0 0 2px; }
.help-sub   { color:rgba(255,255,255,.42); font-size:11.5px; margin:0 0 9px; }
.help-contacts { display:flex; flex-direction:column; gap:4px; margin-bottom:11px; }
.help-contact-link { color:#7eb8ff; font-size:12px; text-decoration:none; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; transition:color .15s; }
.help-contact-link:hover { color:#fff; }
.help-contact-btn { display:block; text-align:center; background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:7px; border-radius:7px; text-decoration:none; transition:background .18s; }
.help-contact-btn:hover { background:#0059d6; color:#fff; }

/* -- Overlay ------------------------------------------------------------- */
.sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:98; cursor:pointer; }
.sidebar-overlay.overlay-open { display:block; }

/* -- Logout modal -------------------------------------------------------- */
.logout-modal { border:none; border-radius:16px; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.15); position:relative; }
.modal-dismiss { position:absolute; top:14px; right:14px; width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:transparent; color:#7a90b0; font-size:13px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s; z-index:1; }
.modal-dismiss:hover:not(:disabled) { background:#f4f7fb; color:#1a2b45; }
.modal-dismiss:disabled { opacity:.4; cursor:not-allowed; }
.logout-body { display:flex; flex-direction:column; align-items:center; padding:44px 32px 24px; text-align:center; }
.logout-icon-wrap { width:68px; height:68px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:26px; margin-bottom:20px; }
.logout-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.logout-sub   { font-size:13px; color:#7a90b0; margin:0; line-height:1.5; }
.logout-footer { display:flex; gap:10px; padding:0 32px 32px; }
.btn-cancel, .btn-confirm { flex:1; display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:10px 16px; border-radius:9px; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; border:none; }
.btn-cancel { background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; }
.btn-cancel:hover:not(:disabled) { background:#e4eaf2; }
.btn-confirm { background:#e03535; color:#fff; }
.btn-confirm:hover:not(:disabled) { background:#c02828; }
.btn-cancel:disabled, .btn-confirm:disabled { opacity:.5; cursor:not-allowed; }

/* -- Vue modals ---------------------------------------------------------- */
.modal-overlay       { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open  { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.5); backdrop-filter:blur(2px); cursor:pointer; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:440px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.2); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-header-styled { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-title-styled  { font-size:15px; font-weight:700; color:#1a2b45; }
.modal-close-btn     { width:28px; height:28px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; flex-shrink:0; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-body-styled   { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
.modal-footer-styled { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
.me-1 { margin-right:4px; }

/* -- Form fields --------------------------------------------------------- */
.form-group-styled  { display:flex; flex-direction:column; gap:5px; }
.form-label-styled  { font-size:12.5px; font-weight:600; color:#1a2b45; }
.form-input-styled  { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.pw-wrap  { position:relative; display:flex; align-items:center; }
.pw-wrap .form-input-styled { padding-right:42px; }
.pw-toggle { position:absolute; right:10px; background:none; border:none; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; display:flex; align-items:center; }
.pw-toggle:hover { color:#004AAD; }

/* -- Error box ----------------------------------------------------------- */
.error-box  { margin:0 24px; border:1px solid #fecaca; background:#fff0f0; border-radius:8px; padding:10px 14px; display:flex; flex-direction:column; gap:5px; }
.error-item { font-size:12.5px; color:#e03535; display:flex; align-items:flex-start; gap:6px; }

/* -- Buttons ------------------------------------------------------------- */
.btn-ghost          { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover    { background:#e4eaf2; }
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }

/* -- Clock In / Out ----------------------------------------------------- */
.clockin-wrap   { display:flex; align-items:center; gap:8px; }
.clockin-timer  { display:flex; align-items:center; gap:5px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:5px 10px; }
.clockin-timer-icon { font-size:11px; color:#16a34a; }
.clockin-elapsed { font-size:12px; font-weight:700; color:#16a34a; font-variant-numeric:tabular-nums; letter-spacing:.04em; white-space:nowrap; }
.btn-clockin    { display:inline-flex; align-items:center; gap:7px; font-size:13px; font-weight:700; padding:7px 14px; border-radius:8px; border:none; cursor:pointer; transition:all .18s; white-space:nowrap; font-family:inherit; }
.btn-clockin:disabled { opacity:.6; cursor:not-allowed; }
.btn-clockin--in  { background:#004AAD; color:#fff; box-shadow:0 2px 8px rgba(0,74,173,.25); }
.btn-clockin--in:hover:not(:disabled)  { background:#003b8e; }
.btn-clockin--out { background:#e03535; color:#fff; box-shadow:0 2px 6px rgba(224,53,53,.2); }
.btn-clockin--out:hover:not(:disabled) { background:#c02828; }
.clockin-label  { font-size:13px; }
.clockin-spinner { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; display:inline-block; animation:clockspin .7s linear infinite; flex-shrink:0; }
@keyframes clockspin { to { transform:rotate(360deg); } }

/* -- Clock confirm modal ------------------------------------------------- */
.clock-modal-box  { max-width:440px; }
.clock-modal-hd   { padding:18px 22px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-shrink:0; }
.clock-modal-hd--in  { background:#004AAD; }
.clock-modal-hd--out { background:#e03535; }
.clock-modal-hd-inner { display:flex; align-items:center; gap:12px; }
.clock-modal-hd-icon  { width:38px; height:38px; border-radius:10px; background:rgba(255,255,255,.15); color:#fff; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.clock-modal-title    { font-size:15px; font-weight:700; color:#fff; margin:0 0 2px; }
.clock-modal-sub      { font-size:12px; color:rgba(255,255,255,.7); margin:0; }
.clock-modal-close    { width:30px; height:30px; border-radius:7px; border:none; background:rgba(255,255,255,.15); color:rgba(255,255,255,.8); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:13px; }
.clock-modal-close:hover { background:rgba(255,255,255,.25); }
.clock-modal-bd   { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.clock-modal-ft   { padding:14px 22px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }

/* Location loading */
.clock-geo-loading { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:16px; }
.clock-geo-spinner { width:20px; height:20px; border:2px solid #dde5f0; border-top-color:#004AAD; border-radius:50%; animation:clockspin .7s linear infinite; flex-shrink:0; }
.clock-geo-loading-text { font-size:13px; color:#7a90b0; margin:0; }

/* Location status cards */
.clock-geo-status       { display:flex; align-items:flex-start; gap:12px; border-radius:10px; padding:14px 16px; border:1px solid; }
.clock-geo-status--match   { background:#f0fdf4; border-color:#bbf7d0; }
.clock-geo-status--nomatch { background:#fefce8; border-color:#fde68a; }
.clock-geo-status--error   { background:#fff0f0; border-color:#fecaca; }
.clock-geo-status-icon     { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.clock-geo-status-icon--green { background:#dcfce7; color:#16a34a; }
.clock-geo-status-icon--amber { background:#fef9c3; color:#ca8a04; }
.clock-geo-status-icon--red   { background:#fee2e2; color:#e03535; }
.clock-geo-status-title { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.clock-geo-status-addr  { font-size:12px; color:#7a90b0; margin:0; line-height:1.5; word-break:break-word; }

/* Reason field */
.clock-reason-wrap  { display:flex; flex-direction:column; gap:5px; }
.clock-reason-label { font-size:12.5px; font-weight:600; color:#1a2b45; display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.clock-reason-hint  { font-size:11px; color:#94a3b8; font-weight:400; }
.req                { color:#e03535; }
.clock-reason-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; resize:vertical; font-family:inherit; transition:border-color .15s; }
.clock-reason-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.clock-reason-input:disabled { background:#f4f7fb; }

/* Confirm button */
.btn-clock-confirm { display:inline-flex; align-items:center; gap:7px; font-size:13px; font-weight:700; padding:9px 20px; border-radius:9px; border:none; cursor:pointer; color:#fff; transition:background .15s; font-family:inherit; }
.btn-clock-confirm:disabled { opacity:.5; cursor:not-allowed; }
.btn-clock-confirm--in  { background:#004AAD; }
.btn-clock-confirm--in:hover:not(:disabled)  { background:#003b8e; }
.btn-clock-confirm--out { background:#e03535; }
.btn-clock-confirm--out:hover:not(:disabled) { background:#c02828; }

/* -- Clock In / Out (duplicate guard — replaced block below) */


/* -- Responsive ---------------------------------------------------------- */
@media (max-width: 768px) {
  /* Hide page title to free up space for clock button */
  .topnav-pagetitle { display:none; }
  .mobile-toggle    { display:flex; }
  .user-info        { display:none; }
  .dropdown-chevron { display:none; }

  /* Topnav layout on mobile — logo left, controls fill right */
  .topnav-inner  { justify-content:space-between; gap:8px; padding:0 12px; }
  .topnav-right  { flex:1; justify-content:flex-end; gap:8px; }

  /* Clock In/Out — expands to fill available center space */
  .clockin-wrap   { flex:1; justify-content:flex-end; gap:5px; }
  .btn-clockin    { flex:1; max-width:120px; padding:7px 10px; font-size:12px; font-weight:700; border-radius:8px; text-align:center; justify-content:center; }
  .btn-clockin--in  { background:#004AAD !important; color:#fff !important; border:none !important; }
  .btn-clockin--out { background:#e03535 !important; color:#fff !important; border:none !important; }
  .clockin-timer   { padding:4px 7px; flex-shrink:0; }
  .clockin-elapsed { font-size:10px; }

  .col-auto { width:0 !important; min-width:0 !important; padding:0 !important; overflow:visible; }
  .col      { width:100% !important; max-width:100% !important; }
  .sidebar-fixed { transform:translateX(-100%); transition:transform 0.25s ease; z-index:99; }
  .sidebar-fixed.sidebar-open { transform:translateX(0); }
}

/* -- Floating Chat Button ----------------------------------------------- */
.chat-fab {
  position: fixed;
  bottom: 28px;
  left: 260px;
  right: auto;
  z-index: 950;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #004AAD;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 4px 18px rgba(0,74,173,.38);
  text-decoration: none;
  transition: background .15s, transform .15s, box-shadow .15s;
}
.chat-fab:hover {
  background: #003b8e;
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,74,173,.45);
  text-decoration: none;
}
.chat-fab-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  border-radius: 99px;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid #fff;
}
.chat-fab-pop-enter-active { transition: transform .2s cubic-bezier(.34,1.56,.64,1), opacity .15s; }
.chat-fab-pop-leave-active { transition: transform .12s, opacity .12s; }
.chat-fab-pop-enter-from  { transform: scale(0); opacity: 0; }
.chat-fab-pop-leave-to    { transform: scale(0); opacity: 0; }
@media (max-width: 768px) { .chat-fab { left: 16px; } }

/* -- Lock Screen --------------------------------------------------------- */
.lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 27, 53, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.lock-card {
  background: #fff;
  border-radius: 20px;
  padding: 40px 36px 32px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 24px 64px rgba(0,0,0,.28);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  text-align: center;
}
.lock-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0,74,173,.12);
  color: #004AAD;
  font-size: 26px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  border: 3px solid rgba(0,74,173,.18);
}
.lock-name {
  font-size: 17px;
  font-weight: 700;
  color: #1a2b45;
  margin: 0 0 4px;
}
.lock-subtitle {
  font-size: 13px;
  color: #7a90b0;
  margin: 0 0 24px;
}
.lock-input-wrap {
  position: relative;
  width: 100%;
  margin-bottom: 8px;
}
.lock-input {
  width: 100%;
  padding: 11px 42px 11px 14px;
  border: 1.5px solid #dde5f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1a2b45;
  outline: none;
  background: #f8fafc;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
  box-sizing: border-box;
}
.lock-input:focus {
  border-color: #004AAD;
  box-shadow: 0 0 0 3px rgba(0,74,173,.1);
  background: #fff;
}
.lock-input--error { border-color: #e03535 !important; }
.lock-pw-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  line-height: 1;
}
.lock-pw-toggle:hover { color: #004AAD; }
.lock-error {
  font-size: 12.5px;
  color: #e03535;
  margin: 0 0 12px;
  width: 100%;
  text-align: left;
}
.lock-btn {
  width: 100%;
  padding: 12px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  transition: background .15s;
  font-family: inherit;
}
.lock-btn:hover:not(:disabled) { background: #003b8e; }
.lock-btn:disabled { opacity: .55; cursor: not-allowed; }
.lock-signout {
  font-size: 12.5px;
  color: #94a3b8;
  margin-top: 18px;
  cursor: pointer;
  text-decoration: none;
  transition: color .15s;
}
.lock-signout:hover { color: #e03535; }

/* lock fade transition */
.lock-fade-enter-active { transition: opacity .2s; }
.lock-fade-leave-active { transition: opacity .18s; }
.lock-fade-enter-from,
.lock-fade-leave-to     { opacity: 0; }
</style>